/* ════════════════════════════════════════════════════════════════
   PVL SCROLL — Landing storytelling controller
   Single scroll progress `p` (0..1) drives 7 acts on desktop.
   Vanilla JS, native scroll (no smooth-scroll lib). Sticky timeline
   on ≥1181px (desktop); tablets/mobile (≤1180px) use native flow with
   early-return in render().
   ════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  // Bug 1 — take manual control of scroll restoration. The desktop stage is a
  // sticky scroll-jacked timeline: if the browser restores scrollY>0 on reload
  // or back-nav BEFORE the controller stabilizes, render() paints several acts
  // mid-crossfade stacked over each other (illegible first paint). With manual
  // restoration the browser never restores scroll; boot resets to top and forces
  // one clean render() so the first frame is always the A1 act. Guarded for
  // engines without scrollRestoration support.
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 1180px)');
  const reducedMotion = () => reducedMotionQuery.matches;
  const isMobileViewport = () => mobileQuery.matches;
  // Bug 7 — única fuente de verdad para "el stage está colapsado a flujo natural"
  // (sin timeline sticky scroll-jacked). True en mobile/tablet (≤1180px native flow)
  // O en desktop con reduced-motion. render() y updateStageHeight() ramifican sobre esto
  // para que el jack de ~1000vh y el crossfade por-frame de los actos NUNCA corran
  // en modo flujo estático: el usuario RM en desktop ya no scrollea stage vacío.
  const isStaticFlow = () => isMobileViewport() || reducedMotion();
  const clamp = (n, min = 0, max = 1) => Math.max(min, Math.min(max, n));
  const mapR = (v, a, b) => clamp((v - a) / (b - a));
  const rgbStr = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';
  // Bug 9: presence/inert handoff stays at 0.34 (PRESENCE_THRESHOLD, the tuned
  // no-both-acts-blank crossover floor); keyboard focusability is gated higher at
  // 0.6 so focus never lands on a barely-legible CTA. Two independent signals.
  const PRESENCE_THRESHOLD = 0.34;
  const FOCUS_GATE_THRESHOLD = 0.6;
  // refs #117 — puntos de hidratación de los layers diferidos de A2/A3 en el
  // path animado. El stage jackeado mide ~1000vh, así que 0.01 de `p` ≈ 10vh
  // de scroll real. A2 termina su fade-in en p=0.130 y A3 en p=0.380: arrancar
  // la descarga en 0.02 y 0.20 deja ~110vh y ~180vh de pista antes de que el
  // acto sea legible. Deliberadamente generoso: hidratar de más solo adelanta
  // bytes que el usuario va a ver igual; hidratar tarde enseña un hueco.
  const A2_MEDIA_HYDRATE_AT = 0.02;
  const A3_MEDIA_HYDRATE_AT = 0.20;

  const parseNumbers = (text) => {
    const matches = String(text || '').replace(',', '.').match(/\d+(?:\.\d+)?/g);
    return matches ? matches.map(Number).filter((n) => Number.isFinite(n)) : [];
  };

  const average = (numbers, fallback = 0) => {
    if (!numbers.length) return fallback;
    return numbers.reduce((acc, n) => acc + n, 0) / numbers.length;
  };

  const CREAM = [250, 248, 245];

  // Module-level teardown registry: callbacks pushed here run on pagehide
  // to release listeners/observers (prevents leaks on bfcache & SPA nav).
  const teardown = [];

  // ── Media diferida de los actos (refs #117) ──────────────────
  // Los layers inactivos de A2/A3 se sirven sin `src` (van en data-pvl-src)
  // porque se ocultan con opacity:0 —siguen en el render tree— y en desktop
  // el stage sticky mete los 8 actos DENTRO del viewport desde el primer
  // paint: ahí loading="lazy" es un no-op y el browser se los bajaría todos
  // antes de que el usuario toque nada. Hidratar desde JS es la única forma
  // de controlar el momento.
  // Idempotente: marca el contenedor y asigna srcset ANTES que src, para que
  // el browser resuelva el candidato del srcset y no dispare una request
  // extra por el src base.
  function hydrateDeferredMedia(container) {
    if (!container || container.dataset.pvlMediaHydrated === '1') return;
    container.dataset.pvlMediaHydrated = '1';
    container.querySelectorAll('img[data-pvl-src]').forEach((img) => {
      if (img.dataset.pvlSrcset) {
        img.srcset = img.dataset.pvlSrcset;
        delete img.dataset.pvlSrcset;
      }
      img.src = img.dataset.pvlSrc;
      delete img.dataset.pvlSrc;
    });
  }

  // Bajo flujo estático (mobile/tablet ≤1180px O desktop reduced-motion)
  // render() sale temprano, así que NO hay señal de scroll por frame que
  // dispare la hidratación: hace falta un IntersectionObserver propio. El
  // rootMargin del 300% precarga con varios viewports de antelación, muy por
  // delante de que el acto asome. Sin IntersectionObserver hidratamos ya
  // mismo (fail-open: mejor gastar los bytes que enseñar un layer vacío).
  // Re-invocable desde handleViewportChange: desconecta el observer anterior
  // antes de recrear, así un resize desktop→mobile queda cubierto sin
  // acumular observers.
  let deferredMediaObserver = null;
  let deferredMediaTeardownBound = false;
  function observeDeferredMedia(root) {
    if (deferredMediaObserver) {
      deferredMediaObserver.disconnect();
      deferredMediaObserver = null;
    }
    const targets = root.querySelectorAll('[data-pvl-a2-media], [data-pvl-a3-media]');
    if (!targets.length) return;
    // En desktop animado manda el umbral de progreso dentro de render().
    if (!isStaticFlow()) return;
    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach((t) => hydrateDeferredMedia(t));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        hydrateDeferredMedia(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '300% 0px', threshold: 0 });
    targets.forEach((t) => io.observe(t));
    deferredMediaObserver = io;
    // Un solo callback de teardown para todas las re-creaciones: siempre
    // desconecta el observer VIGENTE, no el de la primera llamada.
    if (!deferredMediaTeardownBound) {
      deferredMediaTeardownBound = true;
      teardown.push(() => {
        if (deferredMediaObserver) deferredMediaObserver.disconnect();
        deferredMediaObserver = null;
        deferredMediaTeardownBound = false;
      });
    }
  }

  // ── Line-by-line reveal: split titles into measured lines and wrap
  //    each in a clip container so CSS can slide them up. Only runs on
  //    nodes that carry `data-pvl-lines`. Tokenizes childNodes so one
  //    level of inline wrappers survives (act titles carry their italic
  //    accent word in <em>, per the cross-act header system). On any
  //    failure the original markup is restored.
  function setupLineReveal(root) {
    if (reducedMotion()) return;
    root.querySelectorAll('[data-pvl-lines]').forEach((el) => {
      if (el.dataset.pvlLinesSet === '1') return;
      const originalHTML = el.innerHTML;
      const text = el.textContent.trim();
      if (!text) return;
      // Tokenize: text nodes → plain words; element nodes → their words
      // tagged with the wrapper element (shallow clone is enough).
      const tokens = [];
      el.childNodes.forEach((node) => {
        const isElement = node.nodeType === Node.ELEMENT_NODE;
        if (!isElement && node.nodeType !== Node.TEXT_NODE) return;
        node.textContent.split(/\s+/).forEach((w) => {
          if (w) tokens.push({ w, wrap: isElement ? node : null });
        });
      });
      if (!tokens.length) return;
      try {
        el.textContent = '';
        // Measure: append each word in a temp inline span (cloning the
        // wrapper so italic metrics measure true), read top.
        const measured = [];
        tokens.forEach((t) => {
          const s = document.createElement('span');
          if (t.wrap) {
            const wrapClone = t.wrap.cloneNode(false);
            wrapClone.textContent = t.w;
            s.appendChild(wrapClone);
          } else {
            s.textContent = t.w;
          }
          s.style.display = 'inline';
          el.appendChild(s);
          el.appendChild(document.createTextNode(' '));
          measured.push({ t, top: s.getBoundingClientRect().top });
        });
        // Group by approximate top (tolerance for subpixel variance).
        const lines = [];
        let currentLine = null;
        let lastTop = null;
        measured.forEach(({ t, top }) => {
          if (lastTop === null || Math.abs(top - lastTop) > 2) {
            currentLine = [];
            lines.push(currentLine);
            lastTop = top;
          }
          currentLine.push(t);
        });
        // Rebuild: replace children with wrapped lines, restoring each
        // token's inline wrapper inside the line.
        el.textContent = '';
        lines.forEach((line) => {
          const wrap = document.createElement('span');
          wrap.className = 'pvl-line-wrap';
          const inner = document.createElement('span');
          inner.className = 'pvl-line-inner';
          line.forEach((t, i) => {
            if (t.wrap) {
              const wrapClone = t.wrap.cloneNode(false);
              wrapClone.textContent = t.w;
              inner.appendChild(wrapClone);
            } else {
              inner.appendChild(document.createTextNode(t.w));
            }
            if (i < line.length - 1) inner.appendChild(document.createTextNode(' '));
          });
          wrap.appendChild(inner);
          el.appendChild(wrap);
        });
        el.dataset.pvlLinesSet = '1';
      } catch (splitErr) {
        el.innerHTML = originalHTML;
      }
    });
  }

  // On mobile (native flow, no is-active from controller) an IntersectionObserver
  // drives the same `is-revealed` hook. Desktop uses `.pvl-act.is-active` directly,
  // so we MUST skip the IO there: in the sticky timeline every act shares the same
  // geometric region from page load, so the IO would fire on every title at once
  // and the line-reveal animation would burn off while the user is still on the
  // hero — by the time they scroll into the act the title is already revealed.
  function observeLineReveal(root) {
    if (!isMobileViewport()) return;
    if (!('IntersectionObserver' in window)) return;
    const targets = root.querySelectorAll('[data-pvl-lines]');
    if (!targets.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.35 });
    targets.forEach((t) => io.observe(t));
    teardown.push(() => io.disconnect());
  }

  // ── Programas MOBILE reveal-on-scroll ───────────────────────
  // Each layer in the mobile longform stack starts hidden (opacity 0
  // + translateY 20px from CSS) and fades up when entering the
  // viewport. Small stagger between siblings so the four blocks read
  // as a sequence, not a single flash. Reduced-motion users get the
  // CSS-level skip (see media query) — this function still runs but
  // adds is-revealed instantly with no transition cost.
  function observeProgramasReveal(root) {
    if (!isMobileViewport()) return;
    const panel = root.querySelector('[data-pvl-prog-panel]');
    if (!panel) return;
    const layers = Array.from(panel.querySelectorAll('[data-pvl-prog-layer]'));
    if (!layers.length) return;
    // Mark the panel so the CSS failsafe rule (which keeps layers
    // visible when JS doesn't run) yields control to the IO logic.
    panel.dataset.pvlProgRevealable = '1';
    if (typeof IntersectionObserver === 'undefined') {
      layers.forEach((l) => l.classList.add('is-revealed'));
      return;
    }
    if (reducedMotion()) {
      layers.forEach((l) => l.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        // Light per-layer stagger keyed on DOM order.
        const i = parseInt(entry.target.dataset.pvlProgLayer || '0', 10);
        setTimeout(() => entry.target.classList.add('is-revealed'), i * 90);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -10% 0px' });
    layers.forEach((l) => io.observe(l));
  }

  // ── Manifesto MOBILE quote ritual ────────────────────────────
  // Mobile bypasses the sticky timeline (controller early-returns) so the
  // scroll-driven char reveal in render() never fires. Without this the
  // quote would either appear instantly (CSS opacity:1 override) or stay
  // invisible (if the override is bypassed). Native flow needs its own
  // ritual: IntersectionObserver fires when the blockquote enters viewport
  // and we cascade `.is-on` per char with a stagger.
  function setupA7MobileQuoteReveal(root) {
    if (!isMobileViewport()) return;
    const quote = root.querySelector('.pvl-a7__quote[data-pvl-chars="quote"]');
    if (!quote) return;
    // Idempotency: skip if already bound (resize desktop→mobile re-invokes
    // this from handleViewportChange; we MUST NOT double-bind the IO).
    if (quote.dataset.pvlA7MobileBound === '1') return;
    // If desktop scroll-driven reveal already completed (is-revealed set),
    // short-circuit — chars are already shown via CSS, no ritual needed.
    if (quote.classList.contains('is-revealed')) {
      quote.dataset.pvlA7MobileBound = '1';
      return;
    }
    const chars = Array.from(quote.querySelectorAll('.pvl-char'));
    if (!chars.length) return;
    quote.dataset.pvlA7MobileBound = '1';
    if (reducedMotion()) {
      chars.forEach((ch) => ch.classList.add('is-on'));
      quote.classList.add('is-revealed');
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      // SSR / very old browsers: show fully revealed without ritual.
      chars.forEach((ch) => ch.classList.add('is-on'));
      quote.classList.add('is-revealed');
      return;
    }
    let triggered = false;
    const timers = [];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || triggered) return;
        triggered = true;
        io.unobserve(entry.target);
        const stepMs = 70;
        chars.forEach((ch, i) => {
          timers.push(setTimeout(() => ch.classList.add('is-on'), i * stepMs));
        });
        timers.push(setTimeout(() => quote.classList.add('is-revealed'), chars.length * stepMs + 80));
      });
    }, { threshold: 0.35 });
    io.observe(quote);
    teardown.push(() => {
      io.disconnect();
      timers.forEach(clearTimeout);
    });
  }

  // ── Manifesto word-proximity (desktop hover) ──────────────────
  // Words near the cursor stay luminous (opacity 1, slight letter-spacing),
  // words far from it dim (opacity 0.3). Only fires AFTER the scroll reveal
  // completes (blockquote carries `.is-revealed`). Early-returns on mobile
  // and reduced-motion so the quote stays fully visible there.
  function setupManifestoProximity(root) {
    if (isMobileViewport() || reducedMotion()) return;
    const quote = root.querySelector('.pvl-a7__quote[data-pvl-chars="quote"]');
    if (!quote) return;
    // Idempotency: handleViewportChange may re-invoke this on resize
    // mobile→desktop. Avoid double-binding mouse listeners.
    if (quote.dataset.pvlA7HoverBound === '1') return;
    quote.dataset.pvlA7HoverBound = '1';

    let rects = [];
    let rafPending = false;
    let lastX = 0;
    let lastY = 0;
    let leaveTimer = null;

    function refreshRects() {
      rects = Array.from(quote.querySelectorAll('.pvl-char')).map((el) => {
        const r = el.getBoundingClientRect();
        return { el, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
      });
    }

    function apply() {
      rafPending = false;
      const radius = 140;
      rects.forEach((r) => {
        const dx = r.cx - lastX;
        const dy = r.cy - lastY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const prox = clamp(1 - dist / radius);
        r.el.style.setProperty('--pvl-word-prox', prox.toFixed(3));
      });
    }

    function onEnter() {
      if (!quote.classList.contains('is-revealed')) return;
      // Cancel any pending leave timer so rapid leave→enter doesn't
      // remove .is-hovered while the cursor is still over the element.
      if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
      refreshRects();
      quote.classList.add('is-hovered');
    }
    function onMove(e) {
      if (!quote.classList.contains('is-hovered')) return;
      lastX = e.clientX;
      lastY = e.clientY;
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(apply);
    }
    function onLeave() {
      // Keep .is-hovered for 220ms so the CSS transition fires on exit
      // (opacity: dimmed → 1). Removing it immediately would snap because
      // the transition rule only applies while .is-hovered is present.
      // Store the timer ID so onEnter can cancel it on rapid re-entry.
      if (leaveTimer) clearTimeout(leaveTimer);
      leaveTimer = setTimeout(() => {
        leaveTimer = null;
        quote.classList.remove('is-hovered');
        rects.forEach((r) => r.el.style.removeProperty('--pvl-word-prox'));
      }, 220);
    }
    const onResize = () => { if (quote.classList.contains('is-hovered')) refreshRects(); };

    quote.addEventListener('mouseenter', onEnter);
    quote.addEventListener('mousemove', onMove, { passive: true });
    quote.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', onResize, { passive: true });

    teardown.push(() => {
      if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
      quote.removeEventListener('mouseenter', onEnter);
      quote.removeEventListener('mousemove', onMove);
      quote.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', onResize);
    });
  }

  // ── Word-by-word reveal helper ────────────────────────────────
  function setupCharReveal(root) {
    root.querySelectorAll('[data-pvl-chars]').forEach((el) => {
      if (el.dataset.pvlCharsSet) return;
      const text = el.textContent.trim();
      el.textContent = '';
      el.dataset.pvlCharsSet = '1';
      const words = text.split(/\s+/);
      words.forEach((word, i) => {
        const wrap = document.createElement('span');
        wrap.className = 'pvl-word-wrap';
        const span = document.createElement('span');
        span.className = 'pvl-char';
        span.textContent = word;
        wrap.appendChild(span);
        el.appendChild(wrap);
        if (i < words.length - 1) {
          el.appendChild(document.createTextNode(' '));
        }
      });
    });
  }

  // Hero words scroll-driven: same philosophy as revealChars but with
  // stronger blur + lift for the large italic heading. Receives progress
  // 0..1 mapped from scroll within the hero range.
  function revealHeroWords(el, progress) {
    const words = el.querySelectorAll('.pvl-word');
    const n = words.length;
    if (!n) return;
    if (reducedMotion()) {
      words.forEach((w) => {
        w.style.opacity = '1';
        w.style.filter = 'none';
        w.style.transform = 'none';
      });
      return;
    }
    const stagger = 0.6;
    const span = 1 - stagger;
    words.forEach((w, i) => {
      const start = (i / Math.max(n - 1, 1)) * stagger;
      const p = clamp((progress - start) / span);
      w.style.opacity = p;
      w.style.filter = `blur(${((1 - p) * 10).toFixed(2)}px)`;
      w.style.transform = `translateY(${((1 - p) * 16).toFixed(2)}px)`;
    });
  }

  function revealChars(el, progress) {
    const chars = el.querySelectorAll('.pvl-char');
    const n = chars.length;
    // Once fully revealed (mapR clamps to 1), yield inline control to CSS
    // so hover-proximity can take over. Scroll back out → reverse.
    const done = progress >= 0.99;
    if (done) {
      // Always clear residual inline styles when entering the done branch —
      // not gated by `is-revealed` add — so a previous active-reveal frame
      // never leaves blur/opacity stuck on a char (regression risk: B5).
      chars.forEach((ch) => {
        ch.classList.add('is-on');
        if (ch.style.opacity) ch.style.opacity = '';
        if (ch.style.filter) ch.style.filter = '';
      });
      if (!el.classList.contains('is-revealed')) {
        el.classList.add('is-revealed');
      }
      el.dataset.pvlCharsLastP = '1';
      return;
    }
    if (el.classList.contains('is-revealed')) {
      el.classList.remove('is-revealed');
      el.classList.remove('is-hovered');
    }
    // Skip inline writes if progress hasn't moved meaningfully — same
    // throttle the controller uses (0.0005). Cuts ~60Hz style thrash.
    const last = parseFloat(el.dataset.pvlCharsLastP);
    if (Number.isFinite(last) && Math.abs(last - progress) < 0.0005) return;
    el.dataset.pvlCharsLastP = progress.toFixed(4);
    chars.forEach((ch, i) => {
      const p = clamp((progress - (i / n) * 0.65) / 0.35);
      ch.classList.toggle('is-on', p > 0.5);
      if (!reducedMotion()) {
        ch.style.opacity = p;
        ch.style.filter = `blur(${(1 - p) * 4}px)`;
      }
    });
  }

  // Single owner of `.is-active` + `aria-hidden` + `inert`/tabindex shadow per act.
  // Equality guards avoid re-writing the same value 60×/sec while the threshold
  // is sticky (was a confirmed source of MutationObserver thrash on render).
  function setActAccessibility(act, isInteractive) {
    if (!act) return;

    const wasInteractive = act.classList.contains('is-active');
    if (wasInteractive !== isInteractive) {
      act.classList.toggle('is-active', isInteractive);
    }

    const desiredHidden = isInteractive ? 'false' : 'true';
    if (act.getAttribute('aria-hidden') !== desiredHidden) {
      act.setAttribute('aria-hidden', desiredHidden);
    }

    if ('inert' in act) {
      const desiredInert = !isInteractive;
      if (act.inert !== desiredInert) act.inert = desiredInert;
      return;
    }

    const focusables = act.querySelectorAll(FOCUSABLE_SELECTOR);
    focusables.forEach((el) => {
      if (isInteractive) {
        if (!el.hasAttribute('data-pvl-orig-tabindex')) return;
        const prev = el.getAttribute('data-pvl-orig-tabindex');
        if (prev === '__none__') el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', prev || '0');
        el.removeAttribute('data-pvl-orig-tabindex');
        return;
      }

      if (!el.hasAttribute('data-pvl-orig-tabindex')) {
        const current = el.getAttribute('tabindex');
        el.setAttribute('data-pvl-orig-tabindex', current === null ? '__none__' : current);
      }
      if (el.getAttribute('tabindex') !== '-1') {
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  // Bug 9 — INVARIANT 9: decoupled keyboard focus-gate (independent of inert).
  // Presence/inert is owned by setActAccessibility at the 0.34 crossover floor
  // (tuned so adjacent acts are NEVER both inert → no all-blank stage). THIS gate
  // is a SEPARATE concern keyed at opacity > 0.6 (FOCUS_GATE_THRESHOLD): between
  // 0.34 and 0.6 an act stays present, visible, non-inert and mouse-clickable, but
  // its focusable descendants leave the tab order so keyboard focus never lands on
  // a barely-legible CTA about to be Enter-activated. A transient crossfade window
  // where BOTH adjacent acts sit in 0.34-0.6 (present, not inert, none focusable)
  // is ALLOWED — Tab simply flows to page landmarks (nav/skip/progress/footer);
  // that is not a focus trap. Uses a distinct data attribute so it never collides
  // with the inert-fallback tabindex shadow in setActAccessibility above.
  function setActFocusGate(act, isFocusable) {
    if (!act) return;
    const focusables = act.querySelectorAll(FOCUSABLE_SELECTOR);
    focusables.forEach((el) => {
      if (isFocusable) {
        if (!el.hasAttribute('data-pvl-focusgate-tabindex')) return;
        const prev = el.getAttribute('data-pvl-focusgate-tabindex');
        if (prev === '__none__') el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', prev || '0');
        el.removeAttribute('data-pvl-focusgate-tabindex');
        return;
      }

      if (!el.hasAttribute('data-pvl-focusgate-tabindex')) {
        const current = el.getAttribute('tabindex');
        el.setAttribute('data-pvl-focusgate-tabindex', current === null ? '__none__' : current);
      }
      if (el.getAttribute('tabindex') !== '-1') {
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  function setProgramasLayerAccessibility(root, activeIdx) {
    if (!root) return;
    const panel = root.querySelector('[data-pvl-prog-panel]');
    if (!panel) return;

    const isMobile = isMobileViewport();
    const mode = isMobile ? 'mobile' : 'desktop';
    const idx = String(activeIdx);
    if (panel.dataset.pvlProgA11yMode === mode && panel.dataset.pvlProgA11yIdx === idx) return;

    panel.dataset.pvlProgA11yMode = mode;
    panel.dataset.pvlProgA11yIdx = idx;

    const layers = root.querySelectorAll('[data-pvl-prog-layer]');
    const navs = root.querySelectorAll('[data-pvl-prog-nav]');

    layers.forEach((layer, i) => {
      const isActiveLayer = i === activeIdx;
      const isAccessible = isMobile || isActiveLayer;
      if (!isMobile) layer.classList.toggle('is-on', isActiveLayer);

      const desiredHidden = isAccessible ? 'false' : 'true';
      if (layer.getAttribute('aria-hidden') !== desiredHidden) {
        layer.setAttribute('aria-hidden', desiredHidden);
      }

      if ('inert' in layer) {
        const desiredInert = !isAccessible;
        if (layer.inert !== desiredInert) layer.inert = desiredInert;
        return;
      }

      layer.querySelectorAll(FOCUSABLE_SELECTOR).forEach((el) => {
        if (isAccessible) {
          if (!el.hasAttribute('data-pvl-prog-orig-tabindex')) return;
          const prev = el.getAttribute('data-pvl-prog-orig-tabindex');
          if (prev === '__none__') el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', prev || '0');
          el.removeAttribute('data-pvl-prog-orig-tabindex');
          return;
        }

        if (!el.hasAttribute('data-pvl-prog-orig-tabindex')) {
          const current = el.getAttribute('tabindex');
          el.setAttribute('data-pvl-prog-orig-tabindex', current === null ? '__none__' : current);
        }
        if (el.getAttribute('tabindex') !== '-1') {
          el.setAttribute('tabindex', '-1');
        }
      });
    });

    navs.forEach((nav, i) => {
      const active = i === activeIdx;
      nav.classList.toggle('is-on', active);
      nav.setAttribute('aria-selected', String(active));
      if (!isMobile) nav.setAttribute('tabindex', active ? '0' : '-1');
      nav.removeAttribute('aria-pressed');
    });
  }

  function getProgramasLayerIndex(progress) {
    if (progress < 0.862) return 0;
    if (progress < 0.874) return 1;
    if (progress < 0.887) return 2;
    return 3;
  }

  // ── Main scroll controller ────────────────────────────────────
  function boot() {
    const stage = document.querySelector('[data-pvl-stage]');
    if (!stage) return;
    stage.dataset.pvlReady = '0';

    try {

    const sticky = stage.querySelector('[data-pvl-sticky]');
    const signaturePath = stage.querySelector('[data-pvl-signature-stem]');
    if (signaturePath) {
      // <path pathLength="1"> normalizes userSpace length to 1 — drive the
      // dash directly in [0..1] space without measuring getTotalLength().
      if (reducedMotion()) {
        signaturePath.style.strokeDasharray = 'none';
        signaturePath.style.strokeDashoffset = '0';
      } else {
        signaturePath.style.strokeDasharray = '1';
        signaturePath.style.strokeDashoffset = '1';
      }
    }
    // Foliage — one leaf per act + two tendrils, positioned dynamically along
    // the stem path via getPointAtLength so leaves are perfectly attached.
    // data-stem-pos (0..1) → (x, y) on stem; data-side (±1) → which side; the
    // tangent at that point gives the leaf rotation (perpendicular to stem).
    // Under reduced-motion, leaves snap to scale(1) and we deliberately do NOT
    // set inline style.opacity — the @media (prefers-reduced-motion) CSS rule
    // (opacity:1) governs visibility. Setting inline opacity:0 here would beat
    // CSS specificity and freeze the foliage invisible since render() bails early.
    const vineFoliage = (() => {
      const stem = stage.querySelector('[data-pvl-signature-stem]');
      if (!stem) return [];
      const total = stem.getTotalLength ? stem.getTotalLength() : 0;
      if (!total) return [];
      const isRM = reducedMotion();
      return Array.from(stage.querySelectorAll('[data-pvl-signature] [data-leaf], [data-pvl-signature] [data-tendril]'))
        .map((el) => {
          const pos = parseFloat(el.getAttribute('data-stem-pos')) || 0;
          const side = parseFloat(el.getAttribute('data-side')) || 1;
          const at = parseFloat(el.getAttribute('data-reveal-at')) || 0;
          const point = stem.getPointAtLength(total * pos);
          // Tangent → angle along stem; perpendicular = stem normal.
          // SVG y-axis is inverted (down = +y), so the stem's "right side"
          // (visually east when stem moves south) is tangent − 90°. Side=+1
          // → right of stem direction, side=−1 → left.
          const ahead = stem.getPointAtLength(Math.min(total, total * pos + 1));
          const tangentDeg = Math.atan2(ahead.y - point.y, ahead.x - point.x) * 180 / Math.PI;
          const normalDeg = tangentDeg + (side >= 0 ? -90 : 90);
          // Leaf path is drawn with its BASE (peduncle) at (0,0) and tip in +x.
          // Anchoring the base ON the stem and rotating to the normal makes
          // the leaf grow outward FROM the stem when scale(0)→scale(1).
          const x = point.x.toFixed(2);
          const y = point.y.toFixed(2);
          const base = `translate(${x},${y}) rotate(${normalDeg.toFixed(1)})`;
          if (isRM) {
            el.setAttribute('transform', `${base} scale(1)`);
          } else {
            el.setAttribute('transform', `${base} scale(0)`);
            el.style.opacity = '0';
          }
          return { el, base, at };
        });
    })();
    const cursor = document.querySelector('[data-pvl-cursor]');
    const acts = Array.from(stage.querySelectorAll('[data-pvl-act]'));
    const indexButtons = Array.from(stage.querySelectorAll('[data-pvl-index-target]'));
    const atelierIndex = stage.querySelector('[data-pvl-atelier-index]');

    // Pre-setup char reveals + measure line splits + hook IntersectionObserver
    setupCharReveal(stage);
    setupLineReveal(stage);
    setupHeroWords(stage);
    observeLineReveal(stage);
    observeProgramasReveal(stage);
    setupManifestoProximity(stage);
    setupA7MobileQuoteReveal(stage);
    // refs #117 — trigger de media diferida para flujo estático (no-op en el
    // path animado, donde render() hidrata por umbral de progreso).
    observeDeferredMedia(stage);

    // A4 mobile: chips selector. Tap en chip → activa step correspondiente.
    // Desktop usa el render loop con sub-ranges sobre el quad 2×2; mobile
    // muestra un solo step a la vez via .is-current. Coherente con A2/A5.
    // Default: chip 02 (Shampoo) activo — primer paint NO es el opcional.
    // Idempotente: dataset.pvlA4ChipsBound previene doble-attach al cruzar
    // breakpoints (resize desktop→mobile). Llamado en boot Y en
    // handleViewportChange cuando crucemos a mobile.
    const setupA4Chips = () => {
      if (!isMobileViewport()) return;
      const a4Mobile = stage.querySelector('[data-pvl-act="pretratamiento"]');
      if (!a4Mobile) return;
      const nav = a4Mobile.querySelector('[data-pvl-a4-nav]');
      if (!nav || nav.dataset.pvlA4ChipsBound === '1') return;
      const chips = a4Mobile.querySelectorAll('[data-pvl-a4-nav] .pvl-a4__chip');
      const stepsMobile = a4Mobile.querySelectorAll('.pvl-a4__step');
      const progressNum = a4Mobile.querySelector('[data-progress-current]');
      const progressTotal = a4Mobile.querySelector('[data-progress-total]');
      const progressOptional = a4Mobile.querySelector('[data-progress-optional]');
      // #11: total de chips dinámico, no hardcoded.
      if (progressTotal) progressTotal.textContent = String(chips.length);
      const setStep = (idx) => {
        chips.forEach((c, i) => {
          const isActive = i === idx;
          c.classList.toggle('is-active', isActive);
          // #2: aria-current="step" en chip activo. Inactivos NO tienen
          // el atributo (no setear "false" — es ruido para SR).
          if (isActive) c.setAttribute('aria-current', 'step');
          else c.removeAttribute('aria-current');
        });
        stepsMobile.forEach((s, i) => s.classList.toggle('is-current', i === idx));
        a4Mobile.dataset.current = idx;
        if (progressNum) progressNum.textContent = String(idx + 1).padStart(2, '0');
        // #5: legenda "*opcional" solo visible cuando el chip opcional
        // (idx 0) está activo.
        if (progressOptional) {
          if (idx === 0) progressOptional.removeAttribute('hidden');
          else progressOptional.setAttribute('hidden', '');
        }
      };
      chips.forEach((chip, i) => {
        const onChipClick = () => setStep(i);
        chip.addEventListener('click', onChipClick);
        teardown.push(() => chip.removeEventListener('click', onChipClick));
      });
      // Sync inicial: aplicar estado del chip default (02 / idx 1) o el
      // que tenga is-active en el HTML.
      const defaultIdx = Array.from(chips).findIndex((c) => c.classList.contains('is-active'));
      setStep(defaultIdx >= 0 ? defaultIdx : 1);
      nav.dataset.pvlA4ChipsBound = '1';
    };
    setupA4Chips();

    // Cursor glow (desktop only) — both listeners go through teardown so
    // pagehide / bfcache restore doesn't leak a live mousemove handler.
    if (cursor && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const onCursorMove = (e) => {
        cursor.classList.add('is-active');
        cursor.style.left = e.clientX + 'px';
        cursor.style.top = e.clientY + 'px';
      };
      const onCursorLeave = () => cursor.classList.remove('is-active');
      window.addEventListener('mousemove', onCursorMove, { passive: true });
      window.addEventListener('mouseleave', onCursorLeave);
      teardown.push(() => window.removeEventListener('mousemove', onCursorMove));
      teardown.push(() => window.removeEventListener('mouseleave', onCursorLeave));
    }

    // ── Bug 8 — Skip-link asistido por JS ───────────────────────
    // El stage es sticky y mide ~9000px; la navegación nativa por fragmento
    // (#pvl-stage-end) NO mueve scroll ni foco de forma fiable sobre un sticky.
    // CORRECCIÓN 2: NO usar getBoundingClientRect del ancla — vive DENTRO del
    // contexto sticky, así que su rect.top es CONSTANTE (~353) en cualquier
    // scrollY → la fórmula scrollY+rect daba un offset roto (mid-page). En su
    // lugar saltamos al contenido REAL post-stage: la siguiente sección
    // .shopify-section (seo-homepage-content), que es un hermano normal tras el
    // stage y SÍ tiene offsetTop fiable. Fallback: fondo del documento.
    // Tras el scroll, focus() en el ancla mueve activeElement sin re-scrollear.
    // Acción de teclado → scroll instantáneo (behavior:auto). href nativo degrada.
    const skipLink = document.querySelector('[data-pvl-skip-link]');
    const skipTarget = document.querySelector('[data-pvl-skip-target]');
    if (skipLink && skipTarget) {
      // Offset documental real sumando offsetTop por la cadena de offsetParent
      // (NO getBoundingClientRect, que en contexto sticky no refleja la posición
      // en el documento).
      const docOffsetTop = (el) => {
        let y = 0;
        for (let n = el; n; n = n.offsetParent) y += n.offsetTop;
        return y;
      };
      const onSkip = (e) => {
        e.preventDefault();
        // Destino: la sección hermana posterior al stage (seo-homepage-content).
        const landingSection = stage.closest('.shopify-section');
        let dest = landingSection && landingSection.nextElementSibling;
        while (dest && !dest.classList.contains('shopify-section')) dest = dest.nextElementSibling;
        const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const targetY = dest ? Math.min(docOffsetTop(dest), maxScroll) : maxScroll;
        window.scrollTo({ top: targetY, behavior: 'auto' });
        // foco al ancla (tabindex=-1) sin re-scrollear: queda en contenido
        // post-stage, el siguiente Tab sigue por SEO/footer del tema.
        skipTarget.focus({ preventScroll: true });
      };
      skipLink.addEventListener('click', onSkip);
      teardown.push(() => skipLink.removeEventListener('click', onSkip));
    }

    // ── Scroll progress + render loop ───────────────────────────
    let p = 0;
    let lastP = -1;
    let ticking = false;

    const getActEl = (name) => acts.find((a) => a.dataset.pvlAct === name);

    const a1 = getActEl('hero');
    const a2 = getActEl('adhesivos');
    const a3 = getActEl('pestanas');
    const a4 = getActEl('pretratamiento');
    const a5 = getActEl('pinzas');
    const a6 = getActEl('academia');
    const aProgramas = getActEl('programas');
    const a7 = getActEl('manifiesto');
    const actPeaks = {
      hero: 0,
      adhesivos: 0.20,
      pestanas: 0.45,
      pretratamiento: 0.65,
      pinzas: 0.79,
      programas: 0.87,
      academia: 0.94,
      manifiesto: 0.98,
    };

    // Ambient orbs — subtle scroll-driven parallax adds depth behind the acts.
    // Uses the CSS `translate` property (independent of `transform`) so the
    // base translate(-50%, -50%) + pulse keyframes stay intact.
    const orb1 = stage.querySelector('.pvl-orb--1');
    const orb2 = stage.querySelector('.pvl-orb--2');
    const orb3 = stage.querySelector('.pvl-orb--3');


    const a3NavItems = a3 ? Array.from(a3.querySelectorAll('[data-pvl-lash-nav]')) : [];
    const a5NavItems = a5 ? Array.from(a5.querySelectorAll('[data-pvl-pinza-nav]')) : [];

    // Hero video — stable autoplay loop on all platforms (respects reduced-motion).
    // Previously desktop used scroll-scrub which left the video frozen at frame 0
    // when the user did not scroll. Autoplay gives a live first impression without
    // the scrub jank that regla #32 warns against.
    const heroVideo = a1 ? a1.querySelector('video') : null;
    const swapSeq = { a2: 0, a3: 0, a5: 0 };

    // ── Single source of truth for click-target progress positions ──
    // These are the centre-of-window progress values for each spotlight /
    // chip in acts 2/3/5. The render loop uses parallel range thresholds
    // (a2: <0.21 / <0.25 / else; a3: <0.41 / <0.45 / <0.49 / else; a5:
    // 0.745 + i*0.015) — those thresholds and these click targets MUST
    // stay in sync, so they live next to each other here. If you move a
    // spotlight, update both blocks.
    const spotRanges  = [0.205, 0.235, 0.275];
    const lashRanges  = [0.385, 0.430, 0.470, 0.510];
    const pinzaRanges = [0.745, 0.760, 0.775, 0.790, 0.805, 0.820];
    // Programas sub-ranges: 4 click targets. Two constraints drive the
    // numbers: (1) pinzas.outA runs 0.825-0.865 so the entry crossfade
    // dominates that span — Layer 01 needs to reach a clean fully-opaque
    // moment AFTER the pinzas fade is done; (2) Layer 03 needs enough clean
    // dwell to be readable before normal wheel/touchpad deltas can skip it;
    // (3) Layer 04 must finish its
    // dwell BEFORE programas.outA starts at 0.900. To get both, programas
    // inA is shortened to 0.825-0.855 (programas at 100% opacity from
    // 0.855 onward, pinzas at <2% by then). Thresholds [0.862, 0.874, 0.887]:
    //   L01 0.825-0.862 (37vh: 30vh inA + 7vh clean opaque),
    //   L02 0.862-0.874 (12vh clean), L03 0.874-0.887 (13vh clean),
    //   L04 0.887-0.900 (13vh clean) + fade-out 0.900-0.920 inside outA.
    const progRanges  = [0.844, 0.868, 0.881, 0.894];

    function ensureHeroVideoMode() {
      if (!heroVideo) return;
      if (reducedMotion()) {
        heroVideo.pause();
        heroVideo.loop = false;
        if (heroVideo.readyState >= 1) {
          try {
            heroVideo.currentTime = 0;
          } catch (_) {}
        }
        return;
      }

      const tryPlay = () => {
        const maybe = heroVideo.play();
        if (maybe && typeof maybe.catch === 'function') maybe.catch(() => {});
      };
      heroVideo.loop = true;
      heroVideo.defaultMuted = true;
      heroVideo.muted = true;
      heroVideo.playsInline = true;
      if (heroVideo.readyState >= 2) tryPlay();
      else heroVideo.addEventListener('canplay', tryPlay, { once: true });
    }

    function updateStageHeight() {
      // Bug 7 — bajo flujo estático (mobile O reduced-motion) el stage colapsa a
      // altura natural via CSS; nunca forzar el jack de ~1000vh aquí. Guard interno
      // (robusto frente a los guards por call-site, que solo cubrían mobile).
      if (isStaticFlow()) return;
      const viewportH = Math.max(window.innerHeight, 1);
      const innerScrollExtraPx = acts.reduce((acc, act) => {
        const inner = act.querySelector('.pvl-act__inner');
        if (!inner) return acc;
        return acc + Math.max(0, inner.scrollHeight - inner.clientHeight);
      }, 0);

      const baseVh = isMobileViewport() ? 1180 : 1000;
      const extraVh = (innerScrollExtraPx / viewportH) * 100;
      const minVh = isMobileViewport() ? 1180 : 1000;
      const maxVh = isMobileViewport() ? 2600 : 1800;
      const stageHeightVh = clamp(baseVh + extraVh * 0.85, minVh, maxVh);
      stage.style.setProperty('--pvl-stage-height', `${Math.round(stageHeightVh)}vh`);
    }

    function applyLatestSwap(key, swapEls, applyContent) {
      const seq = ++swapSeq[key];
      swapEls.forEach((el) => {
        if (el) el.classList.add('is-swap');
      });
      applyContent();
      requestAnimationFrame(() => {
        if (swapSeq[key] !== seq) return;
        swapEls.forEach((el) => {
          if (el) el.classList.remove('is-swap');
        });
      });
    }

    function computeWindowProgress() {
      const rect = stage.getBoundingClientRect();
      const scrollable = stage.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return 0;
      return clamp(-rect.top / scrollable);
    }

    function scrollToProgress(targetP, immediate = false) {
      const scrollable = stage.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const rect = stage.getBoundingClientRect();
      const absoluteTop = window.scrollY + rect.top;
      const targetY = absoluteTop + scrollable * clamp(targetP);
      window.scrollTo({ top: Math.round(targetY), behavior: immediate || reducedMotion() ? 'auto' : 'smooth' });
    }

    function updateAtelierIndex(activeName) {
      const activeIdx = indexButtons.findIndex((button) => button.dataset.pvlIndexTarget === activeName);
      indexButtons.forEach((button, idx) => {
        const active = idx === activeIdx;
        button.classList.toggle('is-next', idx === activeIdx + 1);
        // Guarda de igualdad: render() corre por frame y reescribir aria-current
        // 60 veces/s invalida el árbol de accesibilidad sin cambiar nada.
        const hasCurrent = button.hasAttribute('aria-current');
        if (active && !hasCurrent) button.setAttribute('aria-current', 'step');
        else if (!active && hasCurrent) button.removeAttribute('aria-current');
      });
    }

    indexButtons.forEach((button) => {
      const onIndexClick = (event) => {
        const targetName = button.dataset.pvlIndexTarget;
        const targetAct = getActEl(targetName);
        if (!targetAct || !Number.isFinite(actPeaks[targetName])) return;
        updateAtelierIndex(targetName);
        if (isStaticFlow()) {
          targetAct.scrollIntoView({ behavior: 'auto', block: 'start' });
          return;
        }
        scrollToProgress(actPeaks[targetName], event.detail === 0);
      };
      button.addEventListener('click', onIndexClick);
      teardown.push(() => button.removeEventListener('click', onIndexClick));
    });
    if (atelierIndex && typeof IntersectionObserver !== 'undefined') {
      const stageEnd = skipTarget;
      let stageVisible = true;
      let stageEndVisible = false;
      const syncIndexVisibility = () => {
        const hidden = !stageVisible || stageEndVisible;
        if (hidden && atelierIndex.contains(document.activeElement) && skipTarget) skipTarget.focus({ preventScroll: true });
        atelierIndex.classList.toggle('is-stage-hidden', hidden);
        // La formación de los trazos es de un solo disparo: se arma la primera
        // vez que la espina es visible, nunca al volver a entrar en pantalla.
        if (!hidden) atelierIndex.classList.add('is-formed');
      };
      const indexObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === stage) stageVisible = entry.isIntersecting;
          if (entry.target === stageEnd) stageEndVisible = entry.isIntersecting;
        });
        syncIndexVisibility();
      }, { rootMargin: '0px 1px' });
      indexObserver.observe(stage);
      if (stageEnd) indexObserver.observe(stageEnd);
      teardown.push(() => {
        indexObserver.disconnect();
        atelierIndex.classList.remove('is-stage-hidden', 'is-formed');
      });
      // Banda de activación al 25-35% del viewport. DEBE seguir conteniendo el
      // ancla del 30% que usa handleViewportChange; si mueves una, mueve la otra.
      // El CSS oculta la espina por debajo de 1366px, no de 1180px, así que en
      // el tramo 1181-1365px esta guarda deja pasar trabajo sobre un elemento
      // display:none. Es inofensivo (updateAtelierIndex solo toca clases y
      // aria-current) pero los dos ficheros discrepan sobre dónde existe la
      // espina: el umbral de 1366px vive únicamente como literal en el
      // @media del CSS. Si se unifica, hay que nombrarlo en un solo sitio.
      const staticActObserver = new IntersectionObserver((entries) => {
        const activeEntry = entries.find((entry) => entry.isIntersecting);
        if (activeEntry && isStaticFlow() && !isMobileViewport()) updateAtelierIndex(activeEntry.target.dataset.pvlAct);
      }, { rootMargin: '-25% 0px -65% 0px' });
      acts.forEach((act) => staticActObserver.observe(act));
      teardown.push(() => staticActObserver.disconnect());
    } else if (atelierIndex) {
      atelierIndex.classList.add('is-stage-hidden');
    }

    // Bug 7 — limpieza de flujo estático (reduced-motion en desktop O mobile).
    // Corre UNA vez por toggle de layout (handleViewportChange pone lastP=-1, así
    // render() re-entra y la invoca). Limpia todo estilo inline que el timeline
    // animado pudo dejar pineado para que el bloque CSS de flujo estático
    // (opacity:1 !important, flujo natural) gobierne, y des-inerta + libera el
    // focus-gate de TODOS los actos: ahora son secciones visibles en orden de
    // documento — ninguno oculto, cada CTA accesible por teclado. NUNCA setea
    // opacity:0; limpia a '' para que el estado visible !important del CSS gane.
    function rmStaticCleanup() {
      // Capas decorativas solo-scroll: neutralizar transforms/draw inline.
      if (signaturePath) signaturePath.style.strokeDashoffset = '';
      vineFoliage.forEach((f) => { f.el.style.opacity = ''; });
      if (orb1) orb1.style.translate = '';
      if (orb2) orb2.style.translate = '';
      if (orb3) orb3.style.translate = '';
      // Internos del hero que el path animado escribe inline.
      if (a1) {
        const halo = a1.querySelector('.pvl-a1__halo');
        const scrollHint = a1.querySelector('.pvl-a1__scroll-hint');
        const topstats = a1.querySelector('.pvl-a1__topstats');
        if (halo) halo.style.transform = '';
        if (scrollHint) scrollHint.style.opacity = '';
        if (topstats) { topstats.style.opacity = ''; topstats.style.pointerEvents = ''; }
        a1.querySelectorAll('[data-pvl-hero-words] .pvl-word').forEach((w) => {
          w.style.opacity = '';
          w.style.filter = '';
          w.style.transform = '';
        });
        a1.querySelectorAll('[data-pvl-reveal]').forEach((el) => {
          el.style.opacity = '';
          el.style.transform = '';
          el.style.visibility = '';
        });
      }
      // Cada acto: limpiar opacity/transform/visibility inline para que gane el
      // estado visible !important del CSS, luego des-inertar (setActAccessibility
      // true → quita aria-hidden/inert) Y liberar el focus-gate 0.6 de Slice 2
      // (setActFocusGate true → libera el shadow tabindex). CROSS-SLICE: sin este
      // setActFocusGate(act,true), un CTA podría quedar pegado en tabindex=-1 bajo
      // flujo estático. Superset seguro también en mobile (actos ya !important-vis).
      acts.forEach((act) => {
        act.style.opacity = '';
        act.style.transform = '';
        act.style.visibility = '';
        setActAccessibility(act, true);
        setActFocusGate(act, true);
      });

      // Bug 7 (corrección programas tablist) — el acto programas es un tablist
      // ARIA con roving tabindex en el path animado: 1 tab=0, resto=-1, navegación
      // por flechas (correcto cuando los 4 paneles crossfadean, solo 1 visible).
      // Pero bajo flujo estático el bloque CSS muestra las 4 .pvl-ap__layer
      // apiladas a la vez → el roving deja 3 tabs fuera del orden de Tab aunque
      // sus paneles se vean (solo alcanzables por flechas). Degradamos a lista
      // plana: las 4 tabs tabbables (tabindex=0) + aria-selected honesto (todas
      // sus rutas están visibles). Estos tabindex=-1 vienen del markup/roving, NO
      // del focus-gate de Slice 2 (su data-pvl-focusgate-tabindex es null), así que
      // setActFocusGate no los toca — hay que neutralizarlos aquí.
      // RESTORE RM-OFF: invalidamos el cache de a11y (pvlProgA11yMode='static') para
      // que el path animado, al re-renderizar (handleViewportChange pone lastP=-1 →
      // render() → setProgramasLayerAccessibility), detecte mismatch de cache y
      // vuelva a establecer el roving correcto (active=0, resto=-1) + aria-selected.
      if (aProgramas) {
        const progPanel = aProgramas.querySelector('[data-pvl-prog-panel]');
        // Fix a11y: en flujo estático las 4 rutas están visibles simultáneamente.
        // Todos los tabs pasan a tabindex=0 (lista plana, accesibles por Tab).
        // ARIA exige un solo tab activo en el tablist — usamos el primero (índice 0)
        // y ponemos aria-selected="false" en el resto para no crear estado inválido.
        aProgramas.querySelectorAll('[data-pvl-prog-nav]').forEach((nav, i) => {
          if (nav.getAttribute('tabindex') !== '0') nav.setAttribute('tabindex', '0');
          const shouldBeSelected = i === 0;
          const current = nav.getAttribute('aria-selected');
          if (current !== String(shouldBeSelected)) nav.setAttribute('aria-selected', String(shouldBeSelected));
        });
        if (progPanel) {
          progPanel.dataset.pvlProgA11yMode = 'static';
          progPanel.dataset.pvlProgA11yIdx = '';
        }
      }
    }

    function render() {
      ticking = false;
      // Ley única de ordenamiento (INVARIANTE 7): bajo flujo estático
      // (mobile/tablet ≤1180px native flow O desktop reduced-motion) los actos son flujo natural
      // sin timeline sticky. CSS fuerza opacity:1/transform:none con !important;
      // hacemos cleanup+un-inert+all-focusable UNA vez por toggle y salimos —
      // nunca computar progreso ni tocar el crossfade por-frame. El cleanup se
      // gatea con lastP===-1 (sentinela que handleViewportChange/boot ponen en
      // cada toggle), luego lastP=0 para que los siguientes frames de scroll
      // retornen barato sin re-churn de DOM (preserva el early-return barato que
      // mobile tenía antes). Si no es estático → path animado (presencia 0.34 +
      // focus-gate 0.6).
      if (isStaticFlow()) {
        if (lastP === -1) {
          rmStaticCleanup();
          lastP = 0;
        }
        return;
      }
      p = computeWindowProgress();
      if (Math.abs(p - lastP) < 0.0005) return;
      lastP = p;
      const interactiveOpacityByAct = Object.create(null);

      // ── SVG signature vine — scroll-driven growth
      // Stem draws as a continuous Bezier (pathLength="1" → dash in [0..1] space),
      // completing at p=0.95 so the line is "done" before the manifesto fully arrives.
      // Each leaf/tendril fades+scales in over a 5% window centred on its
      // data-reveal-at threshold, evoking foliage emerging as the vine grows.
      if (!reducedMotion()) {
        if (signaturePath) {
          const draw = mapR(p, 0, 0.95);
          signaturePath.style.strokeDashoffset = (1 - draw).toFixed(4);
        }
        for (let i = 0; i < vineFoliage.length; i++) {
          const f = vineFoliage[i];
          const t = mapR(p, f.at - 0.04, f.at + 0.01);
          f.el.style.opacity = t.toFixed(3);
          f.el.setAttribute('transform', `${f.base} scale(${(0.4 + 0.6 * t).toFixed(3)})`);
        }
      }

      // ── Ambient orbs parallax (disabled under reduced motion)
      if (!reducedMotion()) {
        if (orb1) orb1.style.translate = `0 ${(p * -60).toFixed(1)}px`;
        if (orb2) orb2.style.translate = `0 ${(p * -110).toFixed(1)}px`;
        if (orb3) orb3.style.translate = `0 ${(p * -160).toFixed(1)}px`;
      }


      // ── Background: ALWAYS cream/white (light theme only)
      if (sticky) sticky.style.backgroundColor = rgbStr(CREAM);
      stage.dataset.mode = 'light';

      // ── Cross-act accent bleed: during narrow boundary windows between
      // acts, the INCOMING act's eyebrow "breathes in" via letter-spacing
      // expansion. Triangular peak at the boundary midpoint, tapers to
      // zero at edges. Conservative B-medium strength.
      let bleedT = 0;
      let bleedTarget = '';
      const bleedWindows = [
        [0.085, 0.125, 'a2'],
        [0.315, 0.355, 'a3'],
        [0.535, 0.575, 'a4'],
        [0.715, 0.755, 'a5'],
        [0.825, 0.865, 'aprogramas'],
        [0.890, 0.930, 'a6'],
        [0.955, 0.990, 'a7'],
      ];
      for (let bi = 0; bi < bleedWindows.length; bi++) {
        const w = bleedWindows[bi];
        if (p >= w[0] && p <= w[1]) {
          const t = (p - w[0]) / (w[1] - w[0]);
          bleedT = 1 - Math.abs(t - 0.5) * 2;
          bleedTarget = w[2];
          break;
        }
      }
      if (stage.dataset.pvlBleed !== bleedTarget) stage.dataset.pvlBleed = bleedTarget;
      stage.style.setProperty('--pvl-bleed-t', bleedT.toFixed(3));

      // ── ACT 1 HERO — tight out, no overlap with Act 2
      if (a1) {
        const op = 1 - easeOut(mapR(p, 0.08, 0.125));
        interactiveOpacityByAct.hero = op;
        a1.style.opacity = op;
        a1.style.pointerEvents = op > 0.1 ? 'auto' : 'none';
        const halo = a1.querySelector('.pvl-a1__halo');
        const scrollHint = a1.querySelector('.pvl-a1__scroll-hint');
        // Bug 6 — the topstats pill is position:absolute; z-index:5 with an
        // opaque backdrop-blur background (rgba(255,255,255,.72)), so fading the
        // A1 act alone never hides it: its own stacking context + opaque bg keep
        // it legible over A2. Drive its opacity here, bound to the A1 fade-out.
        const topstats = a1.querySelector('.pvl-a1__topstats');

        if (reducedMotion()) {
          // Reduced-motion users get the hero in its final visible state
          // immediately. Skip every scroll-driven write below — the inline
          // styles would otherwise pin opacity:0 / transform:translateY(24px)
          // and the CSS @media reduced-motion rules can't override them
          // (no !important; inline wins). See pvl-scroll-v4.css ~2747.
          //
          // Also clear any inline opacity/transform we may have written on
          // [data-pvl-reveal] BEFORE the user toggled reduce-motion mid
          // session (handleViewportChange forces lastP=-1 so we re-enter
          // here on the next frame regardless of progress delta).
          if (halo) halo.style.transform = '';
          if (scrollHint) scrollHint.style.opacity = '';
          // Bug 6 — clear any inline opacity/pointer-events the animated path
          // wrote on the pill before the user toggled reduce-motion mid-session,
          // so the CSS default (visible pill) is restored under RM.
          if (topstats) {
            topstats.style.opacity = '';
            topstats.style.pointerEvents = '';
          }
          a1.querySelectorAll('[data-pvl-reveal]').forEach((el) => {
            el.style.opacity = '';
            el.style.transform = '';
            // Also clear visibility: the normal render path sets visibility:hidden
            // at opacity=0. If the user toggles reduce-motion mid-session the RM
            // early-return runs but visibility may still be 'hidden' from the
            // previous frame, leaving the element invisible with no recovery path.
            el.style.visibility = '';
          });
        } else {
          if (halo) halo.style.transform = `translate(-50%,-50%) scale(${1 + p * 1.2})`;
          if (scrollHint) scrollHint.style.opacity = 1 - easeOut(mapR(p, 0.015, 0.05));

          // ATF — heading default-visible. El CSS base (.pvl-a1 .pvl-word)
          // mantiene el H1 visible en reposo: el above-the-fold debe estar
          // completo en el primer paint, no esperar scroll. En la ventana
          // p ≤ 0.105 NO se escriben inline styles (se limpian residuos para
          // que el CSS mande); por encima revealHeroWords escribe t≈1 — mismo
          // estado visual — y el scroll-back vuelve a limpiar hacia el CSS.
          const heading = a1.querySelector('[data-pvl-hero-words]');
          if (heading) {
            if (p > 0.105) {
              revealHeroWords(heading, easeOut(mapR(p, 0, 0.105)));
            } else {
              heading.querySelectorAll('.pvl-word').forEach((w) => {
                w.style.opacity = '';
                w.style.filter = '';
                w.style.transform = '';
              });
            }
          }

          // ATF — reveal cascade default-visible. El CSS base
          // (.pvl-a1 [data-pvl-reveal]) mantiene eyebrow/kicker/CTAs visibles
          // en reposo. En la ventana p ≤ 0.085 NO se escriben inline styles
          // (se limpian residuos para que el CSS mande — y los CTAs quedan
          // enfocables desde el primer paint); por encima de la ventana los
          // applyReveal escriben t≈1 — mismo estado visual. Acts 2-8 no
          // cambian. Mobile (<=989px) sigue cubierto por sus !important.
          const reveal1 = a1.querySelector('[data-pvl-reveal="1"]');
          const reveal3 = a1.querySelector('[data-pvl-reveal="3"]');
          const reveal4 = a1.querySelector('[data-pvl-reveal="4"]');
          const applyReveal = (el, t) => {
            if (!el) return;
            el.style.opacity = t;
            el.style.transform = `translateY(${(1 - t) * 24}px)`;
            // visibility:hidden removes p=0 elements from focus order (WCAG 2.4.7).
            // Mobile CSS overrides this via visibility:visible !important.
            el.style.visibility = t === 0 ? 'hidden' : '';
          };
          if (p > 0.085) {
            applyReveal(reveal1, easeOut(mapR(p, 0,     0.025)));
            applyReveal(reveal3, easeOut(mapR(p, 0.025, 0.06)));
            applyReveal(reveal4, easeOut(mapR(p, 0.05,  0.085)));
          } else {
            [reveal1, reveal3, reveal4].forEach((el) => {
              if (!el) return;
              el.style.opacity = '';
              el.style.transform = '';
              el.style.visibility = '';
            });
          }

          // Bug 6 — fade the topstats pill out a touch AHEAD of the hero act so
          // its opaque backdrop-blur bg never lingers legibly over A2. The hero
          // op fades 0.08→0.125 and A2 crosses its 0.34 interactive threshold
          // around p≈0.115; mapping the pill to 0.05→0.105 makes it reach
          // opacity:0 by p=0.105 — gone before A2 is legible, no residual
          // overlap. pointer-events:none once it is effectively invisible.
          if (topstats) {
            const tsOp = 1 - easeOut(mapR(p, 0.05, 0.105));
            topstats.style.opacity = tsOp.toFixed(3);
            topstats.style.pointerEvents = tsOp > 0.02 ? 'auto' : 'none';
          }
        }
      }

      // Bug 7 — el viejo slideshow scroll-driven de reduced-motion (un acto
      // opacity:1, el resto opacity:0 + inert, stage seguía en ~1000vh) vivía aquí.
      // ELIMINADO: render() ahora sale temprano bajo isStaticFlow() (que incluye
      // reduced-motion) hacia rmStaticCleanup(), colapsando el stage a flujo natural
      // con los 8 actos visibles en orden. Este branch quedaba inalcanzable bajo RM.

      // ── ACT 2 ADHESIVOS — single-owner window (no overlap fight)
      // Crossfade handoff with a3: outA ends 0.010 PAST the a2/a3 boundary
      // midpoint (0.345 → 0.355); a3.inA starts 0.030 EARLIER (0.315) for
      // the matching half. The 0.020+ lead is required for cubic easeOut
      // crossover to stay above the 0.34 interactive threshold across the
      // full handoff window — see ACT BOUNDARY DOCTRINE in each per-act block.
      if (a2) {
        // inA shifted to 0.085→0.130 (was 0.095→0.16) so the act starts ramping
        // 0.030 BEFORE the a1/a2 boundary midpoint (0.115). The previous range
        // left a 25-unit window (p≈0.085→0.110) where hero was already under
        // 0.30 op and a2 hadn't passed 0.21 — combined max stayed below the
        // 0.34 interactive threshold and the stage went half-blank. Boundary
        // handoff requires the incoming act to cross 0.34 op before the
        // outgoing act drops below it. Cubic easeOut means the start needs to
        // lead the boundary midpoint by at least 0.020 with these range widths.
        const inA = easeOut(mapR(p, 0.085, 0.130));
        const outA = easeOut(mapR(p, 0.325, 0.355));
        const op = inA * (1 - outA);
        interactiveOpacityByAct.adhesivos = op;
        a2.style.opacity = op;
        a2.style.transform = `translateY(${(1 - inA) * 40}px)`;

        // Spotlight ranges (kept in sync with spotRanges click targets):
        //   p<0.21 : Pure Easy
        //   p<0.25 : Pure Clear
        //   else   : Pure Black
        let spotIdx = 0;
        if (p < 0.21) spotIdx = 0;
        else if (p < 0.25) spotIdx = 1;
        else spotIdx = 2;

        const media = a2.querySelector('[data-pvl-a2-media]');
        // refs #117 — warm-up de los layers diferidos. Ver A2_MEDIA_HYDRATE_AT:
        // ~110vh de pista antes de que el acto llegue a opacidad plena.
        // Idempotente, así que solo el primer frame que cruza el umbral trabaja.
        if (p >= A2_MEDIA_HYDRATE_AT) hydrateDeferredMedia(media);
        if (media && media.dataset.focus !== String(spotIdx)) {
          media.dataset.focus = spotIdx;

          const layers = a2.querySelectorAll('[data-pvl-layer]');
          const navBtns = a2.querySelectorAll('[data-pvl-product]');
          const idxEl = a2.querySelector('[data-pvl-prod-idx]');
          const nameEl = a2.querySelector('[data-pvl-prod-name]');
          const specEl = a2.querySelector('[data-pvl-prod-spec]');
          const idealEl = a2.querySelector('[data-pvl-prod-ideal]');
          const dryingEl = a2.querySelector('[data-pvl-prod-drying]');
          const retentionEl = a2.querySelector('[data-pvl-prod-retention]');
          const climateEl = a2.querySelector('[data-pvl-prod-climate]');
          const vaporEl = a2.querySelector('[data-pvl-prod-vapor]');

          layers.forEach((l, i) => l.classList.toggle('is-on', i === spotIdx));
          navBtns.forEach((b, i) => {
            const active = i === spotIdx;
            b.classList.toggle('is-on', active);
            b.setAttribute('aria-pressed', String(active));
          });

          const target = navBtns[spotIdx];
          if (target) {
            applyLatestSwap('a2', [nameEl, specEl, idealEl, dryingEl, retentionEl, climateEl, vaporEl].filter(Boolean), () => {
              if (idxEl) idxEl.textContent = String(spotIdx + 1).padStart(2, '0');
              if (nameEl) nameEl.textContent = target.dataset.name || '';
              if (specEl) specEl.textContent = target.dataset.spec || '';
              if (idealEl) idealEl.textContent = target.dataset.ideal || '';
              if (dryingEl) dryingEl.textContent = target.dataset.drying || '';
              if (retentionEl) retentionEl.textContent = target.dataset.retention || '';
              if (climateEl) climateEl.textContent = target.dataset.climate || '';
              if (vaporEl) vaporEl.textContent = target.dataset.vapor || '';
            });
          }
        }
      }

      // ── ACT 3 PESTAÑAS — single-owner
      // inA start shifted 0.335 → 0.315 (20-unit lead vs a2/a3 boundary
      // midpoint 0.345). The previous 0.010 lead left a 14-unit window
      // p≈0.334→0.349 where both acts held op<0.34 (cubic easeOut crossover
      // requires the incoming act to gain altitude faster than the outgoing
      // act loses it; 0.020 lead matches the pattern applied to all other
      // boundary handoffs in this controller).
      if (a3) {
        const inA = easeOut(mapR(p, 0.315, 0.38));
        const outA = easeOut(mapR(p, 0.535, 0.570));
        const op = inA * (1 - outA);
        interactiveOpacityByAct.pestanas = op;
        a3.style.opacity = op;
        a3.style.transform = `translateY(${(1 - inA) * 40}px)`;

        // Ranges: 0.36-0.41 = 0, 0.41-0.45 = 1, 0.45-0.49 = 2, 0.49-0.52 = 3
        let activeIdx = 0;
        if (p < 0.41) activeIdx = 0;
        else if (p < 0.45) activeIdx = 1;
        else if (p < 0.49) activeIdx = 2;
        else activeIdx = 3;

        const mediaEl = a3.querySelector('[data-pvl-a3-media]');
        // refs #117 — warm-up de los layers diferidos. Ver A3_MEDIA_HYDRATE_AT:
        // ~180vh de pista antes de que el acto llegue a opacidad plena.
        if (p >= A3_MEDIA_HYDRATE_AT) hydrateDeferredMedia(mediaEl);
        if (mediaEl && mediaEl.dataset.active !== String(activeIdx)) {
          mediaEl.dataset.active = activeIdx;
          const layers = a3.querySelectorAll('[data-pvl-layer]');
          const nameEl = a3.querySelector('[data-pvl-lash-name]');
          const idxEl = a3.querySelector('[data-pvl-lash-idx]');
          const specEl = a3.querySelector('[data-pvl-lash-spec]');
          const tagEl = a3.querySelector('[data-pvl-lash-tag]');
          const navBtns = a3.querySelectorAll('[data-pvl-lash-nav]');

          layers.forEach((l, i) => l.classList.toggle('is-on', i === activeIdx));
          applyLatestSwap('a3', [nameEl, specEl, tagEl], () => {
            const d = a3NavItems[activeIdx]?.dataset || {};
            if (idxEl) idxEl.textContent = d.pvlLashIdx || d.idx || '';
            if (nameEl) nameEl.textContent = d.pvlLashName || d.name || '';
            if (specEl) specEl.textContent = d.pvlLashSpec || d.spec || '';
            if (tagEl) tagEl.textContent = d.pvlLashTag || d.tag || '';
          });
          navBtns.forEach((b, i) => {
            const active = i === activeIdx;
            b.classList.toggle('is-on', active);
            b.setAttribute('aria-pressed', String(active));
          });
        }
      }

      // ── ACT 4 PRE-TRATAMIENTO — narrative timeline + scroll-driven step focus
      // Los 4 pasos son fijos y siempre visibles. .is-active dispara el
      // stagger CSS de entrada. Mientras se scrollea dentro del rango del act,
      // el step "activo" recibe .is-current y crece (thumb + action + número).
      // Crossfade handoff: inA starts 0.020 BEFORE the a3/a4 boundary midpoint
      // (0.555) — symmetric with a3.outA.start (0.535). outA ends 0.005 AFTER
      // the a4/a5 boundary (0.745).
      if (a4) {
        // inA start shifted 0.550→0.535 (15 units earlier) to close the a3/a4
        // boundary dead-zone. Now matches a3.outA start (0.535) so the crossfade
        // is symmetric. Boundary handoff requires the incoming act to cross 0.34
        // op before the outgoing act drops below it. Cubic easeOut means the
        // start needs to lead the boundary midpoint by at least 0.020 with these
        // range widths.
        const inA = easeOut(mapR(p, 0.535, 0.595));
        // outA shifted from 0.705 → 0.715 para que el stage 4 (final lock con
        // las 4 imágenes) tenga ~15 progress-units de full opacity antes de
        // empezar a fadear. Sin esto el user veía el cierre brevemente.
        const outA = easeOut(mapR(p, 0.715, 0.745));
        const op = inA * (1 - outA);
        interactiveOpacityByAct.pretratamiento = op;
        a4.style.opacity = op;
        a4.style.transform = `translateY(${(1 - inA) * 40}px)`;

        // Step focus ranges. Steps 0-2 share equal windows (~0.030 each); step
        // 3 (final "Refuerzo") gets the long tail through the A4 fade-out so
        // the closing editorial breathes before crossfading into A5.
        let curIdx = -1;
        if (p >= 0.595 && p < 0.625) curIdx = 0;
        else if (p >= 0.625 && p < 0.655) curIdx = 1;
        else if (p >= 0.655 && p < 0.685) curIdx = 2;
        else if (p >= 0.685 && p <= 0.745) curIdx = 3;

        if (a4.dataset.current !== String(curIdx)) {
          a4.dataset.current = curIdx;
          const steps = a4.querySelectorAll('.pvl-a4__step');
          steps.forEach((s, i) => s.classList.toggle('is-current', i === curIdx));
        }

        // Quad 2×2 desktop: 5 stages. Default stage 0 (Removedor) cubre
        // TODA la entrada — incluyendo bleed con A3 — para garantizar que
        // el primer producto que vea el user sea Removedor. Stages 1-3
        // arrancan después con ventanas iguales (0.020 cada una) y stage 4
        // (cierre editorial con las 4 imágenes) entra a 0.685 para tener
        // 0.030 de full opacity antes del outA start (0.715), 3x más
        // larga que cada stage intermedio. El stage 4 inicio anterior
        // (0.705) coincidía con A4 ya casi invisible — bug del shift de
        // outA 0.725→0.715 que cerró el dead-zone con A5 sin reajustar
        // las thresholds de stage.
        let stageIdx = 0;
        if (p >= 0.625 && p < 0.645) stageIdx = 1;
        else if (p >= 0.645 && p < 0.665) stageIdx = 2;
        else if (p >= 0.665 && p < 0.685) stageIdx = 3;
        else if (p >= 0.685) stageIdx = 4;

        const quad = a4.querySelector('[data-pvl-a4-quad]');
        if (quad && quad.dataset.stage !== String(stageIdx)) {
          quad.dataset.stage = stageIdx;
        }
      }

      // ── ACT 5 PINZAS — single-owner
      // Crossfade handoff: outA ends 0.020 AFTER the a5/a6 boundary midpoint
      // (0.845 → 0.865); a6.inA starts 0.020 EARLIER (0.825) for the symmetric
      // half — see ACT BOUNDARY DOCTRINE.
      if (a5) {
        // inA start shifted 0.725→0.715 to close the a4/a5 boundary dead-zone
        // at p≈0.724→0.731. Boundary handoff requires the incoming act to cross
        // 0.34 op before the outgoing act drops below it. Cubic easeOut means
        // the start needs to lead the boundary midpoint by at least 0.020 with
        // these range widths.
        const inA = easeOut(mapR(p, 0.715, 0.77));
        const outA = easeOut(mapR(p, 0.825, 0.865));
        const op = inA * (1 - outA);
        interactiveOpacityByAct.pinzas = op;
        a5.style.opacity = op;
        a5.style.transform = `translateY(${(1 - inA) * 40}px)`;

        // 6 ranges across 0.745-0.825 (each 0.015 progress-units). Stretches
        // chip pacing so PRO 6 is reached at p=0.820 — only 5 units before
        // outA starts at 0.825 — instead of arriving at 0.795 and leaving
        // the user staring at the same chip for 30 progress-units (~25% of
        // the act's window) before fade-out.
        const rangeStart = 0.745;
        const span = 0.015;
        let pinzaIdx = 0;
        for (let i = 0; i < 6; i++) {
          if (p >= rangeStart + i * span) pinzaIdx = i;
        }

        const macroEl = a5.querySelector('[data-pvl-a5-macro]');
        if (macroEl && macroEl.dataset.active !== String(pinzaIdx)) {
          macroEl.dataset.active = pinzaIdx;
          const idxEl = a5.querySelector('[data-pvl-pinza-idx]');
          const nameEl = a5.querySelector('[data-pvl-pinza-name]');
          const subEl = a5.querySelector('[data-pvl-pinza-sub]');
          const tagEl = a5.querySelector('[data-pvl-pinza-tag]');
          const benefitsEl = a5.querySelector('[data-pvl-pinza-benefits-text]');
          const specsEl = a5.querySelector('[data-pvl-pinza-specs-text]');
          const idealEl = a5.querySelector('[data-pvl-pinza-ideal-text]');
          const chips = a5.querySelectorAll('[data-pvl-pinza-nav]');
          // Bottles & chips share the equality guard with the macro swap so
          // we don't re-classToggle 60×/sec while the same pinzaIdx is sticky.
          const bottles = a5.querySelectorAll('[data-pvl-a5-bottle]');

          applyLatestSwap('a5', [nameEl, subEl, tagEl], () => {
            const d = a5NavItems[pinzaIdx]?.dataset || {};
            if (idxEl) idxEl.textContent = d.pvlPinzaIdx || d.idx || '';
            if (nameEl) nameEl.textContent = d.pvlPinzaName || d.name || '';
            if (subEl) subEl.textContent = d.pvlPinzaSub || d.sub || '';
            if (tagEl) tagEl.textContent = d.pvlPinzaTag || d.tag || '';
            if (benefitsEl) benefitsEl.textContent = d.pvlPinzaBenefits || '';
            if (specsEl) specsEl.textContent = d.pvlPinzaSpecs || '';
            if (idealEl) idealEl.textContent = d.pvlPinzaIdeal || '';
          });
          chips.forEach((c, i) => {
            const active = i === pinzaIdx;
            c.classList.toggle('is-on', active);
            c.setAttribute('aria-pressed', String(active));
          });
          bottles.forEach((b, i) => b.classList.toggle('is-on', i === pinzaIdx));
        }
      }

      // ── ACT 6 PROGRAMAS — single-owner + 4 sub-range active layer
      // Crossfade handoff: inA starts at the same time as a5.outA (0.825), ends
      // 0.005 AFTER a5.outA ends. outA at 0.900-0.920 hands over to a6 (Blog).
      // Inside the active window, 4 sub-ranges activate one of the 4 program
      // layers + nav buttons. Sub-range boundaries: 01<0.862 / 02<0.874 /
      // 03<0.887 / 04 else. Click targets in progRanges hoisted near boot top.
      if (aProgramas) {
        const inA = easeOut(mapR(p, 0.825, 0.855));
        const outA = easeOut(mapR(p, 0.900, 0.920));
        const op = inA * (1 - outA);
        interactiveOpacityByAct.programas = op;
        aProgramas.style.opacity = op;
        aProgramas.style.transform = `translateY(${(1 - inA) * 40}px)`;

        // Thresholds put L01's switch-out at 0.862 (after the 30vh entry
        // crossfade with pinzas finishes at 0.855, plus 7vh of clean
        // fully-opaque programas). L03 gets the longest clean mid-window so
        // normal wheel/touchpad deltas do not skip it before outA begins.
        const progIdx = getProgramasLayerIndex(p);

        const panel = aProgramas.querySelector('[data-pvl-prog-panel]');
        if (panel) {
          if (panel.dataset.active !== String(progIdx)) panel.dataset.active = progIdx;
          setProgramasLayerAccessibility(aProgramas, progIdx);
        }
      }

      // ── ACT 7 BLOG — single-owner
      // Now occupies the position previously held by Programas. inA crossfades
      // with programas.outA (0.900-0.920). outA ends 0.025 BEFORE the
      // a7/manifiesto boundary midpoint (0.967). Counter range shifted to fit
      // inside the new exclusive window (0.940-0.955).
      if (a6) {
        const inA = easeOut(mapR(p, 0.900, 0.940));
        const outA = easeOut(mapR(p, 0.955, 0.980));
        const op = inA * (1 - outA);
        interactiveOpacityByAct.academia = op;
        a6.style.opacity = op;
        a6.style.transform = `translateY(${(1 - inA) * 40}px)`;

        // Animated counter (inline in lede). Manual thousand separator
        // (es-ES style — dot) avoids toLocaleString rendering "2661" mid-tween
        // on engines/locales that drop the separator below ≥1000.
        const counter = a6.querySelector('[data-pvl-counter]');
        if (counter) {
          const target = parseInt(counter.dataset.pvlCounter, 10) || 2847;
          const t = easeOut(mapR(p, 0.91, 0.95));
          counter.textContent = String(Math.round(target * t)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        }
      }

      // ── ACT 8 MANIFIESTO — single-owner
      // a7 still has no outA (final act, persists to end of stage). inA shifted
      // 0.040 LATER (0.915 → 0.955) so it crossfades with programas.outA.
      // Char-reveal ritual range correspondingly shifted to 0.965-0.985.
      if (a7) {
        const inA = easeOut(mapR(p, 0.955, 0.990));
        interactiveOpacityByAct.manifiesto = inA;
        a7.style.opacity = inA;

        // Char-reveal: is-revealed (progress >= 0.99) fires at global
        // p ≈ 0.985, matching the progress-bar marker at 98%. Lets
        // word-proximity hover take over before the user reaches max scroll.
        const quoteProgress = mapR(p, 0.965, 0.985);
        const quote = a7.querySelector('[data-pvl-chars="quote"]');
        if (quote) revealChars(quote, quoteProgress);
      }

      // Desktop-animated path only — render() already early-returned for mobile
      // (line ~769) and the reduced-motion branch never reaches here, so the
      // focus-gate cannot strip tab order from acts that are in natural flow /
      // fully visible. Two decoupled signals per act (INVARIANT 9):
      //   isPresent  (opacity > 0.34) → aria-hidden/inert handoff — UNTOUCHED.
      //   isFocusable(opacity > 0.6 ) → keyboard tab order only — independent.
      // The focus-gate only owns tab order WHILE the act is present. Once it
      // drops to ≤0.34 the presence layer (inert, or its tabindex fallback on
      // legacy engines) takes over tab-removal, so we release the gate's shadow
      // there — this also avoids a double-tabindex-shadow collision with the
      // inert fallback on browsers without `inert` support.
      let activeIndexAct = 'hero';
      let activeIndexOpacity = -1;
      acts.forEach((act) => {
        const actName = act.dataset.pvlAct;
        const opacity = Number(interactiveOpacityByAct[actName] || 0);
        if (opacity > activeIndexOpacity) {
          activeIndexAct = actName;
          activeIndexOpacity = opacity;
        }
        const isPresent = opacity > PRESENCE_THRESHOLD;
        setActAccessibility(act, isPresent);
        setActFocusGate(act, !isPresent || opacity > FOCUS_GATE_THRESHOLD);
      });
      updateAtelierIndex(activeIndexAct);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(render);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    teardown.push(() => window.removeEventListener('scroll', onScroll));

    // Mobile/reduced-motion safety: render() early-returns on mobile, leaving the
    // counter mid-tween if the user resized down. Force it to its final formatted
    // value so the lede reads "2.847 horas" instead of e.g. "2661".
    function forceCounterFinal() {
      if (!a6) return;
      const counter = a6.querySelector('[data-pvl-counter]');
      if (!counter) return;
      const target = parseInt(counter.dataset.pvlCounter, 10) || 2847;
      counter.textContent = String(target).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    // Hoisted reference so handleViewportChange can clear tilt inline transforms
    // without re-querying. Populated below once tilt setup runs.
    let tiltCardsRef = null;

    const handleViewportChange = () => {
      ensureHeroVideoMode();
      // En mobile no recalculamos stage height (CSS lo pone en auto).
      if (!isMobileViewport()) updateStageHeight();
      // Re-attach chips de A4 si recién cruzamos a mobile (resize desktop→
      // mobile). setupA4Chips() es idempotente — chequea dataset flag.
      setupA4Chips();
      // refs #117 — el IO de media diferida SOLO existe bajo flujo estático,
      // así que hay que re-evaluarlo en cada cambio de viewport/reduced-motion:
      // desktop→mobile lo crea, mobile→desktop lo desconecta y devuelve el
      // control al umbral de progreso de render().
      observeDeferredMedia(stage);
      // Force the throttle gate (Math.abs(p - lastP) < 0.0005) to release
      // on the next render — otherwise toggling prefers-reduced-motion or
      // the mobile breakpoint mid-session would keep the previous frame's
      // inline styles pinned. lastP=-1 guarantees one unconditional pass.
      lastP = -1;
      // When transitioning into mobile, render() will early-return — pin the
      // counter to its final value AND clear any inline transform left on the
      // 3D-tilt cards from the desktop hover handler.
      if (isMobileViewport()) {
        forceCounterFinal();
        const programasPanel = aProgramas && aProgramas.querySelector('[data-pvl-prog-panel]');
        const programasIdx = programasPanel ? parseInt(programasPanel.dataset.active, 10) || 0 : 0;
        setProgramasLayerAccessibility(aProgramas, programasIdx);
        if (Array.isArray(tiltCardsRef)) {
          tiltCardsRef.forEach((card) => { card.style.transform = ''; });
        }
      }
      onScroll();
      if (isStaticFlow() && !isMobileViewport()) requestAnimationFrame(() => {
        const anchorY = window.innerHeight * 0.3;
        const active = acts.find((act) => {
          const rect = act.getBoundingClientRect();
          return rect.top <= anchorY && rect.bottom > anchorY;
        }) || acts[0];
        updateAtelierIndex(active.dataset.pvlAct);
      });

      // ── Manifesto cross-breakpoint reconciliation ──────────────
      // Boot wires setupA7MobileQuoteReveal / setupManifestoProximity
      // ONCE based on the initial viewport. If the user resizes across
      // 989px the wrong handler is bound (or none) and the quote either
      // stays invisible (CSS rule .pvl-a7__quote .pvl-char {opacity:0
      // !important; filter:blur(4px) !important} pinned by the desktop
      // path) or never lights up word-proximity hover. We re-invoke the
      // appropriate setup here; both functions are idempotent (dataset
      // guards) so this is safe to call repeatedly.
      const a7Quote = stage.querySelector('.pvl-a7__quote[data-pvl-chars="quote"]');
      if (a7Quote) {
        if (isMobileViewport()) {
          // Clear residual desktop inline styles on chars so the CSS
          // override (which only applies pre-`.is-on`) doesn't fight
          // with stuck `style.opacity` / `style.filter` from a desktop
          // revealChars frame.
          a7Quote.querySelectorAll('.pvl-char').forEach((el) => {
            el.style.opacity = '';
            el.style.filter = '';
          });
          setupA7MobileQuoteReveal(stage);
          observeProgramasReveal(stage);
        } else {
          // Drop is-on/is-revealed so desktop scroll-driven retakes
          // ownership cleanly on the next render() pass.
          a7Quote.querySelectorAll('.pvl-char.is-on').forEach((el) => {
            el.classList.remove('is-on');
          });
          a7Quote.classList.remove('is-revealed');
          setupManifestoProximity(stage);
        }
      }
    };

    window.addEventListener('resize', handleViewportChange, { passive: true });
    teardown.push(() => window.removeEventListener('resize', handleViewportChange));
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleViewportChange);
      teardown.push(() => mobileQuery.removeEventListener('change', handleViewportChange));
    }
    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', handleViewportChange);
      teardown.push(() => reducedMotionQuery.removeEventListener('change', handleViewportChange));
    }

    ensureHeroVideoMode();
    if (!isMobileViewport()) updateStageHeight();
    // Bug 1 — clean first paint. With scrollRestoration='manual' the browser no
    // longer restores scrollY, but a reload/back-nav can still leave the document
    // at a non-zero scroll position from before the freeze. Reset to the top
    // BEFORE the initial render() so computeWindowProgress() reads p=0 and paints
    // the single clean A1 frame instead of a stacked mid-crossfade. behavior:auto
    // (instant, no smooth-scroll) so there is no visible jump. This runs on every
    // boot() — including the bfcache pageshow re-boot (see pageshow handler) — so
    // back/forward navigation also lands on a clean top paint.
    // ORDERING: goes through the normal render() path; at scrollY=0 the animated
    // desktop path paints A1 fully opaque (op=1). Bajo flujo estático (mobile O
    // desktop reduced-motion) render() sale temprano via isStaticFlow() →
    // rmStaticCleanup() (no-op seguro: colapsa+limpia+des-inerta), así que este
    // reset es seguro en TODOS los modos. isStaticFlow() está definido a nivel de
    // módulo, mucho antes de este forced render — el ordering es correcto.
    window.scrollTo(0, 0);
    render(); // initial (flujo estático → cleanup+return; animado → pinta A1)
    // Mobile boot: render() returned early, so the counter would stay at its
    // SSR'd "2.847" but in case the JS template ever changes that, pin it now.
    if (isMobileViewport()) forceCounterFinal();

    // ── Mobile local-swap helpers ──────────────────────────────
    // On mobile native flow there is no timeline, so nav clicks must
    // swap the visible product/lash/step/pinza locally without scroll.
    function applyA2State(idx) {
      if (!a2) return;
      // refs #117 — red de seguridad: hidratar ANTES de togglear .is-on. Bajo
      // flujo estático el IO tiene margen de sobra, pero un tap muy temprano
      // (o el swipe, que sintetiza este mismo click) podría ganarle la carrera
      // y revelar un layer sin src. hydrateDeferredMedia es idempotente.
      hydrateDeferredMedia(a2.querySelector('[data-pvl-a2-media]'));
      const navBtns = a2.querySelectorAll('[data-pvl-product]');
      const target = navBtns[idx];
      if (!target) return;
      const d = target.dataset;

      const layers = a2.querySelectorAll('[data-pvl-layer]');
      const idxEl = a2.querySelector('[data-pvl-prod-idx]');
      const nameEl = a2.querySelector('[data-pvl-prod-name]');
      const specEl = a2.querySelector('[data-pvl-prod-spec]');
      const idealEl = a2.querySelector('[data-pvl-prod-ideal]');

      layers.forEach((l, i) => l.classList.toggle('is-on', i === idx));
      navBtns.forEach((b, i) => {
        const active = i === idx;
        b.classList.toggle('is-on', active);
        b.setAttribute('aria-pressed', String(active));
      });

      if (idxEl) idxEl.textContent = String(idx + 1).padStart(2, '0');
      if (nameEl) nameEl.textContent = d.name || '';
      if (specEl) specEl.textContent = d.spec || '';
      if (idealEl) idealEl.textContent = d.ideal || '';
      const dryingEl = a2.querySelector('[data-pvl-prod-drying]');
      const retentionEl = a2.querySelector('[data-pvl-prod-retention]');
      const climateEl = a2.querySelector('[data-pvl-prod-climate]');
      const vaporEl = a2.querySelector('[data-pvl-prod-vapor]');
      if (dryingEl) dryingEl.textContent = d.drying || '';
      if (retentionEl) retentionEl.textContent = d.retention || '';
      if (climateEl) climateEl.textContent = d.climate || '';
      if (vaporEl) vaporEl.textContent = d.vapor || '';

      const media = a2.querySelector('[data-pvl-a2-media]');
      if (media) media.dataset.focus = idx;
    }
    function applyA3State(idx) {
      if (!a3) return;
      // refs #117 — misma red de seguridad que en applyA2State: hidratar antes
      // de cualquier toggle de clase para no revelar nunca un layer vacío.
      hydrateDeferredMedia(a3.querySelector('[data-pvl-a3-media]'));
      const layers = a3.querySelectorAll('[data-pvl-layer]');
      const idxEl = a3.querySelector('[data-pvl-lash-idx]');
      const nameEl = a3.querySelector('[data-pvl-lash-name]');
      const specEl = a3.querySelector('[data-pvl-lash-spec]');
      const tagEl = a3.querySelector('[data-pvl-lash-tag]');
      const btns = a3.querySelectorAll('[data-pvl-lash-nav]');
      const d = a3NavItems[idx]?.dataset || {};
      layers.forEach((l, i) => l.classList.toggle('is-on', i === idx));
      btns.forEach((b, i) => {
        const active = i === idx;
        b.classList.toggle('is-on', active);
        b.setAttribute('aria-pressed', String(active));
      });
      if (idxEl) idxEl.textContent = d.pvlLashIdx || '';
      if (nameEl) nameEl.textContent = d.pvlLashName || '';
      if (specEl) specEl.textContent = d.pvlLashSpec || '';
      if (tagEl) tagEl.textContent = d.pvlLashTag || '';
    }
    function applyA5State(idx) {
      if (!a5) return;
      const idxEl = a5.querySelector('[data-pvl-pinza-idx]');
      const nameEl = a5.querySelector('[data-pvl-pinza-name]');
      const subEl = a5.querySelector('[data-pvl-pinza-sub]');
      const tagEl = a5.querySelector('[data-pvl-pinza-tag]');
      const benefitsEl = a5.querySelector('[data-pvl-pinza-benefits-text]');
      const specsEl = a5.querySelector('[data-pvl-pinza-specs-text]');
      const idealEl = a5.querySelector('[data-pvl-pinza-ideal-text]');
      const bottles = a5.querySelectorAll('[data-pvl-a5-bottle]');
      const chips = a5.querySelectorAll('[data-pvl-pinza-nav]');
      const d = a5NavItems[idx]?.dataset || {};
      bottles.forEach((b, i) => b.classList.toggle('is-on', i === idx));
      chips.forEach((c, i) => {
        const active = i === idx;
        c.classList.toggle('is-on', active);
        c.setAttribute('aria-pressed', String(active));
      });
      if (idxEl) idxEl.textContent = d.pvlPinzaIdx || '';
      if (nameEl) nameEl.textContent = d.pvlPinzaName || '';
      if (subEl) subEl.textContent = d.pvlPinzaSub || '';
      if (tagEl) tagEl.textContent = d.pvlPinzaTag || '';
      if (benefitsEl) benefitsEl.textContent = d.pvlPinzaBenefits || '';
      if (specsEl) specsEl.textContent = d.pvlPinzaSpecs || '';
      if (idealEl) idealEl.textContent = d.pvlPinzaIdeal || '';
    }

    // Click/tap on Act 2 product bottles → scroll-to-progress (desktop animado)
    // o swap de contenido in-place (sin timeline). Bug 7: bajo flujo estático
    // (mobile O desktop reduced-motion) NO hay timeline que avanzar, así que
    // scrollToProgress teleportaría a una posición de documento fraccional
    // (scrollY 0→~6293). isStaticFlow() enruta ambos modos al swap local: el nav
    // sigue cambiando el producto activo, sin scroll. El path animado intacto.
    const adhesivosItems = stage.querySelectorAll('[data-pvl-product]');
    // spotRanges/lashRanges/pinzaRanges hoisted near top of boot() — single
    // source of truth shared with the render loop's threshold logic.
    adhesivosItems.forEach((item) => {
      item.addEventListener('click', () => {
        const idx = parseInt(item.dataset.pvlProduct, 10);
        if (isStaticFlow()) { applyA2State(idx); return; }
        const targetP = spotRanges[idx] || 0.2;
        scrollToProgress(targetP);
      });
    });
    // Click on Act 3 lash nav buttons → scroll-to-progress (desktop animado)
    // o swap local (sin timeline). Bug 7: isStaticFlow() enruta mobile Y
    // desktop reduced-motion al swap local — sin teleport de scroll.
    stage.querySelectorAll('[data-pvl-lash-nav]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.pvlLashNav, 10);
        if (isStaticFlow()) { applyA3State(idx); return; }
        const targetP = lashRanges[idx] || 0.38;
        scrollToProgress(targetP);
      });
    });

    // refs #117 — pista extra para la media diferida: apuntar con el ratón al
    // nav o tabular hasta él ya delata la intención de cambiar de layer, bien
    // antes del click. `once` porque hidratar es de un solo uso; el teardown
    // limpia el focusin que nunca llegó a dispararse.
    [
      ['.pvl-a2__nav', '[data-pvl-a2-media]'],
      ['.pvl-a3__nav', '[data-pvl-a3-media]'],
    ].forEach(([navSelector, mediaSelector]) => {
      const nav = stage.querySelector(navSelector);
      const media = stage.querySelector(mediaSelector);
      if (!nav || !media) return;
      const onIntent = () => hydrateDeferredMedia(media);
      nav.addEventListener('pointerenter', onIntent, { once: true, passive: true });
      nav.addEventListener('focusin', onIntent, { once: true, passive: true });
      teardown.push(() => {
        nav.removeEventListener('pointerenter', onIntent);
        nav.removeEventListener('focusin', onIntent);
      });
    });

    // Click on Act 5 pinza chip nav → scroll-to-progress (desktop animado)
    // o swap local (sin timeline). Bug 7: isStaticFlow() enruta mobile Y
    // desktop reduced-motion al swap local — sin teleport de scroll.
    stage.querySelectorAll('[data-pvl-pinza-nav]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.pvlPinzaNav, 10);
        if (isStaticFlow()) { applyA5State(idx); return; }
        const targetP = pinzaRanges[idx] || 0.74;
        scrollToProgress(targetP);
      });
    });

    // Click on Act 7 programa nav → scroll to that program's sub-range.
    // Mobile bails (nav is display:none on <=989px — buttons unreachable
    // because the magazine longform layout shows all 4 layers stacked).
    // progRanges hoisted near top of boot().
    const progNavBtns = stage.querySelectorAll('[data-pvl-prog-nav]');
    progNavBtns.forEach((btn, btnIdx) => {
      btn.addEventListener('click', () => {
        // Bug 7: bajo flujo estático las 4 rutas ya están apiladas y visibles —
        // no hay timeline que avanzar. Suprimir scroll (scrollToProgress
        // teleportaría a posición fraccional). Mobile ya retornaba; ahora desktop
        // reduced-motion también. Path animado intacto.
        if (isStaticFlow()) return;
        const idx = parseInt(btn.dataset.pvlProgNav, 10);
        const targetP = progRanges[idx] || 0.901;
        scrollToProgress(targetP);
      });

      btn.addEventListener('keydown', (e) => {
        // Flechas mueven el foco entre tabs; bajo flujo estático el click que
        // dispara abajo es no-op de scroll (guard isStaticFlow en el click), así
        // que la navegación por teclado funciona sin teleport.
        if (isMobileViewport()) return;

        let nextIdx = null;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIdx = btnIdx - 1;
        else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIdx = btnIdx + 1;
        else if (e.key === 'Home') nextIdx = 0;
        else if (e.key === 'End') nextIdx = progNavBtns.length - 1;
        else return;

        e.preventDefault();
        const targetIdx = (nextIdx + progNavBtns.length) % progNavBtns.length;
        const targetBtn = progNavBtns[targetIdx];
        if (!targetBtn) return;
        targetBtn.focus();
        targetBtn.click();
      });
    });

    // Act 1 CTA "Descubrir el catálogo" — desktop: scroll by progress.
    // Mobile: scrollIntoView del section destino por href (#adhesivos).
    // Only intercept plain left-clicks on `#…` hrefs — modifier keys, middle
    // click and external/non-anchor hrefs keep their browser default behaviour
    // (open in new tab, etc.).
    stage.querySelectorAll('[data-pvl-scroll-to]').forEach((el) => {
      el.addEventListener('click', (e) => {
        const href = el.getAttribute('href') || '';
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (typeof e.button === 'number' && e.button !== 0) return;
        if (!href.startsWith('#')) return;
        e.preventDefault();
        // Bug 7: bajo flujo estático (mobile O desktop reduced-motion) no hay
        // timeline; navegar al section destino real con scrollIntoView (posición
        // de documento ya resuelta en flujo natural) en vez de scrollToProgress,
        // que teleportaría a una posición fraccional. behavior:auto bajo RM.
        if (isStaticFlow()) {
          const target = document.querySelector(href);
          if (target && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
          }
          return;
        }
        const targetP = parseFloat(el.dataset.pvlScrollTo) || 0.18;
        scrollToProgress(targetP);
      });
    });

    // Magnetic CTAs — desktop only. The button follows the cursor slightly
    // within its own bounds, then eases back on leave. Uses CSS `translate`
    // so any future `transform` hover states don't collide.
    if (!reducedMotion() && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const magneticSelectors = [
        '.pvl-a1__cta',
        '.pvl-a2__cta',
        '.pvl-a3__cta',
        '.pvl-a4__cta',
        '.pvl-a5__cta',
        '.pvl-a7__btn',
      ].join(',');
      const magneticStrength = 0.25;
      stage.querySelectorAll(magneticSelectors).forEach((cta) => {
        // The `translate` transition lives in CSS now (each CTA's transition
        // list ends with `translate 260ms cubic-bezier(...)`). Writing it
        // inline here would overwrite the CSS shorthand and reset every
        // other animatable property to the default 0s — confirmed regression.
        //
        // High-polling-rate mice fire mousemove several times per frame, and
        // every call used to force a synchronous layout read
        // (getBoundingClientRect). Coalesce to at most one read+write per
        // frame with the same rafPending flag setupManifestoProximity uses.
        // The rect is still measured inside the frame, so a mid-hover scroll
        // still resolves against fresh geometry and the magnet output is
        // pixel-identical to the unthrottled version.
        let rafPending = false;
        let rafId = 0;
        let lastX = 0;
        let lastY = 0;

        const applyMagnet = () => {
          rafPending = false;
          rafId = 0;
          const r = cta.getBoundingClientRect();
          const x = lastX - (r.left + r.width / 2);
          const y = lastY - (r.top + r.height / 2);
          // Round to integer pixels — sub-pixel translate noise (".0px")
          // is below the perceptible threshold and just clutters DevTools.
          const tx = Math.round(x * magneticStrength);
          const ty = Math.round(y * magneticStrength);
          cta.style.translate = `${tx}px ${ty}px`;
        };

        const onMagnetMove = (e) => {
          lastX = e.clientX;
          lastY = e.clientY;
          if (rafPending) return;
          rafPending = true;
          rafId = requestAnimationFrame(applyMagnet);
        };
        // Cancel any frame still queued from the last move: letting it land
        // after the reset would re-offset a CTA the cursor already left.
        const cancelMagnetFrame = () => {
          if (rafId) cancelAnimationFrame(rafId);
          rafId = 0;
          rafPending = false;
        };
        const onMagnetLeave = () => {
          cancelMagnetFrame();
          cta.style.translate = '';
        };
        cta.addEventListener('mousemove', onMagnetMove);
        cta.addEventListener('mouseleave', onMagnetLeave);
        teardown.push(() => cta.removeEventListener('mousemove', onMagnetMove));
        teardown.push(() => cta.removeEventListener('mouseleave', onMagnetLeave));
        teardown.push(cancelMagnetFrame);

        // Ink-fill: ink center pinned to entry point, grows on hover, collapses on leave.
        // Touch/pen pointers fire pointerenter on tap and stay sticky → in mobile that
        // would leave `is-inking` (text cream) without the desktop ::before backdrop,
        // making the label invisible against the cream page. Restrict to mouse only.
        const onInkEnter = (e) => {
          if (e.pointerType !== 'mouse') return;
          const r = cta.getBoundingClientRect();
          const px = ((e.clientX - r.left) / r.width) * 100;
          const py = ((e.clientY - r.top) / r.height) * 100;
          cta.style.setProperty('--pvl-ink-x', px.toFixed(1) + '%');
          cta.style.setProperty('--pvl-ink-y', py.toFixed(1) + '%');
          cta.classList.add('is-inking');
        };
        const onInkLeave = (e) => {
          if (e.pointerType !== 'mouse') return;
          cta.classList.remove('is-inking');
        };
        cta.addEventListener('pointerenter', onInkEnter);
        cta.addEventListener('pointerleave', onInkLeave);
        teardown.push(() => cta.removeEventListener('pointerenter', onInkEnter));
        teardown.push(() => cta.removeEventListener('pointerleave', onInkLeave));
      });
    }

    // 3D tilt on academia cards (mouse follow). Desktop + hover-capable +
    // motion-allowed only — mobile/touch/reduced-motion devices skip entirely
    // so we don't attach handlers that leak inline transforms or fire on tap.
    const tiltCards = stage.querySelectorAll('[data-pvl-tilt]');
    tiltCardsRef = Array.from(tiltCards);
    if (
      !isMobileViewport() &&
      !reducedMotion() &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ) {
      tiltCards.forEach((card) => {
        const onMove = (e) => {
          const r = card.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width - 0.5;
          const y = (e.clientY - r.top) / r.height - 0.5;
          card.style.transform = `translateY(-6px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg)`;
        };
        const onLeave = () => { card.style.transform = ''; };
        card.addEventListener('mousemove', onMove);
        card.addEventListener('mouseleave', onLeave);
        teardown.push(() => card.removeEventListener('mousemove', onMove));
        teardown.push(() => card.removeEventListener('mouseleave', onLeave));
      });
    }

    stage.dataset.pvlReady = '1';
    bootBannerCounters();
    } catch (error) {
      stage.dataset.pvlReady = '0';
      stage.removeAttribute('data-mode');
      // FOUC: sin la clase pvl-js el CSS pre-init (solo acto 1 visible) deja
      // de aplicar y el fallback apilado html:not(.pvl-js) recupera TODO el
      // contenido en flujo natural — si el boot falló, nadie va a animar el
      // timeline y un stage de 1000vh con un solo acto sería contenido perdido.
      document.documentElement.classList.remove('pvl-js');
      console.error('[PVL] Scroll v4 boot failed. Fallback mode restored.', error);
    }
  }

  // P2 banner — trust band count-up. One-shot on load, synced with the band
  // entrance (720ms delay in CSS). A11y: the REAL value already lives in the
  // DOM for screen readers and JS-off (visually-hidden twin in the snippet);
  // only the aria-hidden visual spans are rewritten here. Reduced-motion
  // users keep the static final values (markup ships them pre-filled).
  function bootBannerCounters() {
    const nums = document.querySelectorAll('[data-pvl-count-to]');
    if (!nums.length || reducedMotion()) return;
    const DURATION = 900;
    const BAND_DELAY = 720;
    nums.forEach((el) => {
      if (el.dataset.pvlCountDone === '1') return;
      el.dataset.pvlCountDone = '1';
      const target = parseFloat(el.dataset.pvlCountTo);
      if (!Number.isFinite(target)) return;
      const dec = parseInt(el.dataset.pvlCountDec || '0', 10);
      const prefix = el.dataset.pvlCountPrefix || '';
      const timer = setTimeout(() => {
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - start) / DURATION);
          const eased = 1 - Math.pow(1 - t, 3);
          el.textContent = prefix + (target * eased).toFixed(dec);
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, BAND_DELAY);
      teardown.push(() => clearTimeout(timer));
    });
  }

  // Hero word-by-word splitter: wraps each word in .pvl-word with --i index
  // so CSS can stagger the animation. Respects whitespace and descenders.
  // Construye todo en un DocumentFragment y reemplaza el contenido en UNA
  // sola operación al final: si algo falla a mitad el texto original permanece
  // intacto (sin H1 vacío visible ni SEO roto).
  function setupHeroWords(root) {
    const container = root.querySelectorAll ? root : document;
    container.querySelectorAll('[data-pvl-hero-words]').forEach((el) => {
      if (el.dataset.pvlHeroWordsSet === '1') return;
      const originalHTML = el.innerHTML;
      const text = el.textContent.trim();
      if (!text) return;
      // Tokeniza por nodos hijos para que los envoltorios inline sobrevivan
      // al troceo (el H1 del banner lleva la palabra acento en <em>): los
      // text nodes se parten en palabras planas; los element nodes aportan
      // sus palabras envueltas en un clon superficial del elemento (un solo
      // nivel de profundidad es suficiente para el contrato del banner).
      const tokens = [];
      el.childNodes.forEach((node) => {
        const isElement = node.nodeType === Node.ELEMENT_NODE;
        if (!isElement && node.nodeType !== Node.TEXT_NODE) return;
        node.textContent.split(/\s+/).forEach((w) => {
          if (w) tokens.push({ word: w, wrap: isElement ? node : null });
        });
      });
      if (!tokens.length) return;
      const frag = document.createDocumentFragment();
      tokens.forEach((t, i) => {
        const span = document.createElement('span');
        span.className = 'pvl-word';
        span.style.setProperty('--i', i);
        if (t.wrap) {
          const wrap = t.wrap.cloneNode(false);
          wrap.textContent = t.word;
          span.appendChild(wrap);
        } else {
          span.textContent = t.word;
        }
        frag.appendChild(span);
        if (i < tokens.length - 1) frag.appendChild(document.createTextNode(' '));
      });
      // Reemplazo atómico: el DOM ve el cambio completo de una sola vez.
      // Si algún paso previo lanzó excepción el markup original no se toca;
      // si falla el propio reemplazo, restauramos el markup original aquí
      // mismo (el catch del boot no tiene acceso al frag ni a cada heading).
      try {
        el.textContent = '';
        el.appendChild(frag);
      } catch (fragErr) {
        el.innerHTML = originalHTML;
        return;
      }
      el.dataset.pvlHeroWordsSet = '1';
    });
  }

  // Blur-resolve numeric: trigger on `.is-active` class (set by the scroll
  // timeline when the act is actually being viewed). IntersectionObserver
  // won't work here because all acts share the same sticky stage region
  // and would all fire at once on load.
  function bootNumScopes() {
    const scopes = document.querySelectorAll('[data-pvl-num-scope]');
    if (!scopes.length) return;

    if (reducedMotion()) {
      scopes.forEach((s) => s.classList.add('pvl-num-scope--revealed'));
      return;
    }

    const activate = (scope) => {
      if (scope.classList.contains('pvl-num-scope--revealed')) return;
      scope.classList.add('pvl-num-scope--revealed');
    };

    const mo = new MutationObserver((records) => {
      records.forEach((rec) => {
        if (rec.type !== 'attributes' || rec.attributeName !== 'class') return;
        const t = rec.target;
        if (t.classList.contains('is-active')) activate(t);
      });
    });

    scopes.forEach((s) => {
      if (s.classList.contains('is-active')) activate(s);
      mo.observe(s, { attributes: true, attributeFilter: ['class'] });
    });

    teardown.push(() => mo.disconnect());
    return mo;
  }

  function runTeardown() {
    while (teardown.length) {
      const fn = teardown.pop();
      try { fn(); } catch (_) { /* swallow teardown errors */ }
    }
  }

  // Idempotent boot wrapper. boot() builds fresh closures + queries DOM each
  // call; setupCharReveal / setupLineReveal / setupHeroWords already have
  // per-element dataset gates so re-entry is safe. We tear down listeners
  // first to avoid double-attach on bfcache restore.
  let isBooted = false;
  function bootAll() {
    if (isBooted) runTeardown();
    boot();
    bootNumScopes();
    setupMobileSwipe();
    setupSwipeHintReveal();
    isBooted = true;
  }

  // ── Swipe-hint per-act reveal ────────────────────────────────────────
  // The "← Desliza →" affordance under each carousel was firing once on
  // page load (5.5s animation, 1.6s delay) — users who took longer than
  // ~7s to scroll into A2/A3/A4/A5 never saw it, so the swipe gesture
  // stayed hidden. IntersectionObserver gates the .is-revealed class to
  // when each hint actually enters the viewport, guaranteeing it plays
  // the moment the user lands on that act. Each hint animates once per
  // session; we unobserve after the first intersection.
  function setupSwipeHintReveal() {
    const hints = document.querySelectorAll('.pvl-swipe-hint');
    if (!hints.length) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Old browsers / SSR: reveal everything statically (CSS RM fallback
      // already handles the no-animation case).
      hints.forEach((h) => h.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.6 });
    hints.forEach((h) => io.observe(h));
    teardown.push(() => io.disconnect());
  }

  // ── Mobile swipe gesture — A2/A3/A5 product carousels ────────────────
  // On mobile (≤989px), the layered product images can be swapped via
  // horizontal swipe gestures over the media container. Reuses the existing
  // click handlers on the nav buttons by simulating a click on the target
  // index, so all the side effects (overlay text, aria-pressed, layer toggle)
  // stay in sync.
  //
  // Listeners are wired unconditionally (no isMobileViewport bail at boot)
  // so a user who resizes the window or rotates the device crosses the
  // breakpoint without us missing the wire-up. The mobile-only constraint
  // is enforced at touchstart time, which is robust to viewport changes.
  function setupMobileSwipe() {
    if (!('ontouchstart' in window) && navigator.maxTouchPoints === 0) return;

    const carousels = [
      { media: document.querySelector('[data-pvl-a2-media]'),   navs: document.querySelectorAll('[data-pvl-product]'),     activeClass: 'is-on' },
      { media: document.querySelector('[data-pvl-a3-media]'),   navs: document.querySelectorAll('[data-pvl-lash-nav]'),    activeClass: 'is-on' },
      { media: document.querySelector('[data-pvl-a5-macro]'),   navs: document.querySelectorAll('[data-pvl-pinza-nav]'),   activeClass: 'is-on' },
      // A4 (Pretratamiento) protocol — chips use 'is-active' instead of 'is-on'.
      // Swipe container is the steps list; users agarran the visible step body
      // with their finger to advance through the 4-step protocol.
      { media: document.querySelector('.pvl-a4__protocol'),     navs: document.querySelectorAll('.pvl-a4__chip[data-step-target]'), activeClass: 'is-active' },
    ];

    const THRESHOLD_X = 40;   // min horizontal travel to count as swipe
    const MAX_VERT    = 60;   // bail if vertical travel exceeds this (user is scrolling)
    const MAX_TIME    = 700;  // bail if gesture takes too long (probably not a swipe)
    const DRAG_VISUAL_CAP = 60; // max px the media translates during finger drag

    carousels.forEach(({ media, navs, activeClass }) => {
      if (!media || !navs.length) return;
      const ACTIVE = activeClass || 'is-on';

      // Preserve vertical page scroll, intercept only horizontal pans.
      media.style.touchAction = 'pan-y';

      let startX = 0, startY = 0, startTime = 0, dragging = false;
      // The gesture splits in two: the index change (functional, always on)
      // and the drag-follow translate + ease-back (decorative). Reduced-motion
      // users keep swiping to change product; they just never get the transform.
      // Latched once per gesture at touchstart so a mid-swipe preference flip
      // can't leave a translate written by touchmove without its reset.
      let animateDrag = false;
      // The ease-back cleanup timer, kept per carousel so it can be cleared on
      // re-entry and on teardown (see setupA7MobileQuoteReveal / bootBannerCounters).
      let resetTimer = null;

      const onTouchStart = (e) => {
        if (!isMobileViewport()) return; // desktop: click handlers are enough
        if (e.touches.length !== 1) return;
        const t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        startTime = Date.now();
        dragging = true;
        animateDrag = !reducedMotion();
      };

      const onTouchMove = (e) => {
        if (!dragging || e.touches.length !== 1) return;
        const t = e.touches[0];
        const dx = t.clientX - startX;
        const dy = Math.abs(t.clientY - startY);
        if (dy > MAX_VERT) {
          // user is clearly scrolling vertically — bail and reset visual
          dragging = false;
          if (animateDrag) media.style.transform = '';
          return;
        }
        if (!animateDrag) return;
        // Subtle drag-follow: media follows the finger up to a cap, with
        // an easing factor so it feels responsive but not slippery.
        const capped = Math.max(-DRAG_VISUAL_CAP, Math.min(DRAG_VISUAL_CAP, dx * 0.35));
        media.style.transform = `translateX(${capped}px)`;
      };

      const reset = () => {
        // Reduced motion never wrote a transform for this gesture, so there is
        // nothing to ease back: no transition, no timer, no style writes.
        if (!animateDrag) return;
        if (resetTimer) clearTimeout(resetTimer);
        media.style.transition = 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)';
        media.style.transform = '';
        resetTimer = setTimeout(() => {
          resetTimer = null;
          media.style.transition = '';
        }, 260);
      };

      const onTouchEnd = (e) => {
        if (!dragging) return;
        dragging = false;
        const end = e.changedTouches[0];
        const dx = end.clientX - startX;
        const dy = Math.abs(end.clientY - startY);
        const dt = Date.now() - startTime;

        if (Math.abs(dx) < THRESHOLD_X || dy > MAX_VERT || dt > MAX_TIME) {
          reset();
          return;
        }

        // Find the currently active nav (configured class); fall back to 0.
        let currentIdx = 0;
        navs.forEach((b, i) => { if (b.classList.contains(ACTIVE)) currentIdx = i; });

        // Swipe left (dx < 0) → next product. Swipe right (dx > 0) → previous.
        // Wrap-around so end → start and vice versa.
        const direction = dx < 0 ? 1 : -1;
        const newIdx = ((currentIdx + direction) + navs.length) % navs.length;

        reset();
        const target = navs[newIdx];
        if (target) target.click();
      };

      const onTouchCancel = () => {
        if (!dragging) return;
        dragging = false;
        reset();
      };

      media.addEventListener('touchstart', onTouchStart, { passive: true });
      media.addEventListener('touchmove', onTouchMove, { passive: true });
      media.addEventListener('touchend', onTouchEnd, { passive: true });
      media.addEventListener('touchcancel', onTouchCancel, { passive: true });

      teardown.push(() => media.removeEventListener('touchstart', onTouchStart));
      teardown.push(() => media.removeEventListener('touchmove', onTouchMove));
      teardown.push(() => media.removeEventListener('touchend', onTouchEnd));
      teardown.push(() => media.removeEventListener('touchcancel', onTouchCancel));
      teardown.push(() => { if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; } });
    });
  }

  // FOUC: bootear en cuanto el script ejecute si el stage ya está parseado —
  // NO esperar a DOMContentLoaded, que en redes lentas queda retenido varios
  // segundos por el resto de scripts defer (tema + analytics + pixels). El
  // tag <script async> va después de todo el DOM del stage en la sección, así
  // que en la práctica este querySelector siempre acierta; el camino DCL
  // queda como red de seguridad si alguien recoloca el script.
  if (document.querySelector('[data-pvl-stage]')) {
    bootAll();
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // bfcache: pagehide with persisted=true means the page is being frozen,
  // not destroyed — listeners survive, so don't tear them down. Only run
  // teardown on real unload (persisted=false). On pageshow with persisted
  // =true the page was restored from cache — listeners may be stale (e.g.
  // mediaQuery change handlers can drift), so we tear down + re-boot to
  // guarantee a clean controller state.
  // Bug 1 (1.3) — this bfcache re-boot path lands on a clean top state for free:
  // bootAll() → boot() runs the same scrollTo(0,0) + forced render() as a cold
  // boot, so back/forward navigation also repaints the clean A1 frame instead of
  // the stale stacked mid-crossfade the browser would otherwise restore.
  window.addEventListener('pagehide', (e) => { if (!e.persisted) runTeardown(); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) bootAll(); });
})();
